import Phaser from 'phaser';
import { ColyseusClient } from '../network/ColyseusClient.js';
import { InputManager } from '../input/InputManager.js';
import { JuiceEffects } from '../effects/JuiceEffects.js';
import { AnnouncerOverlay } from '../effects/AnnouncerOverlay.js';
import { GRID, MATCH, SNAKE_CONFIG } from '@snake-royalle/shared';
import type { ExtractionResult } from '@snake-royalle/shared';
import { disableMenuScroll } from '../utils/menu-scroll.js';
import { sfx } from '../audio/SFX.js';
import { playContext as playMusic, stopMusic } from '../audio/Music.js';
import { GAME_SFX, CHEST_SFX, loadSfxGroup, loadKillVoicelines } from '../audio/sfx-groups.js';
import { CoinChart } from '../effects/CoinChart.js';
import { getRandomPerks } from '../data/perks.js';
import { track, trackPaidPlayStarted, trackDemoEnded } from '../analytics.js';
import { requestShare } from '../share/share-client.js';

// API lives on the game server (Railway), not the page origin (Vercel).
const env = (import.meta as any).env;
const SERVER_HTTP_URL = (env?.VITE_SERVER_URL || 'ws://localhost:2567').replace(/^ws/, 'http');

/** Milliseconds between server state broadcasts */
const STATE_TICK_MS = 1000 / MATCH.tickRate;

/**
 * Milliseconds it takes a snake to move one cell. Snakes move every
 * SNAKE_CONFIG.baseMoveTickInterval server ticks (default 2 = 200ms),
 * so we interpolate sprite positions over this window — not over the
 * 100ms state-broadcast window — otherwise the sprite reaches the new
 * cell in 100ms and then sits idle until the next move arrives,
 * producing the choppy "step-and-pause" feel.
 */
const MOVE_TICK_MS = STATE_TICK_MS * SNAKE_CONFIG.baseMoveTickInterval;
const LOCAL_SMOOTH_MOVE_MS = MOVE_TICK_MS;
const LOCAL_RESPONSIVE_MOVE_MS = Math.max(80, MOVE_TICK_MS * 0.6);
const REMOTE_SMOOTH_MOVE_MS = MOVE_TICK_MS;
const REMOTE_CATCHUP_MOVE_MS = Math.max(150, MOVE_TICK_MS * 0.8);

const CELL_SIZE = GRID.cellSize;

// === Slither animation tuning ===
// Body segments oscillate perpendicular to their local tangent so the snake
// looks alive. Subtle on purpose: the player still has to read grid positions
// for collisions, so the offset stays well under one cell.
const SLITHER_AMP        = 2.2;     // px — ~14% of CELL_SIZE
const SLITHER_FREQ       = 0.008;   // rad/ms — full wave ≈ 785 ms
const SEGMENT_PHASE_STEP = 0.55;    // rad per segment — wave appears to travel head→tail

interface SnakeData {
  id: string;
  playerId: string;
  segments: { x: number; y: number }[];
  direction: string;
  coins: number;
  totalBanked: number;
  extractionsUsed: number;
  isAlive: boolean;
  isExtracted: boolean;
  isBot: boolean;
  isInvulnerable: boolean;
}

interface CoinData {
  id: string;
  position: { x: number; y: number };
  value: number;
  tier: string;
}

interface ZoneData {
  id: string;
  position: { x: number; y: number };
  isActive: boolean;
}

interface DeathParticipantData {
  id: string;
  playerId: string;
  segments: { x: number; y: number }[];
  direction: string;
  isBot: boolean;
}

/** Main gameplay scene — renders the grid, snakes, coins, zones */
export class GameScene extends Phaser.Scene {
  private client!: ColyseusClient;
  private inputManager!: InputManager;
  private mySnakeId: string = '';
  /** snake.id that we last synced the input direction from. Cleared each
   *  time we get a new snake (spawn or respawn) so the next state sync
   *  resets the InputManager's lastDispatched. */
  private inputSyncedFor: string | null = null;
  /** Per-PLAYER leaderboard history (keyed by display name, not snake id —
   *  so a player who dies and rejoins keeps accumulating on top of their
   *  past extractions). Reset implicitly when the GameScene is recreated
   *  for a new match. */
  private leaderboardHistory: Map<string, {
    name: string;
    isBot: boolean;
    bankedFromPastLives: number;       // sum of totalBanked from snakes that died
    currentSnake: { id: string; coins: number; banked: number; alive: boolean } | null;
    /** Highest stake multiplier this player committed across all lives
     *  in this match (mirrors the server's GREATEST semantic). Defaults
     *  to 1; updated by the requestLeaderboardData reconciliation loop
     *  using the per-snake `stakeMultiplier` field broadcast on `state`. */
    stakeMultiplier: number;
  }> = new Map();
  private playerCategory: string = '';
  private _selectedPerk: any = null;
  private demoMode: boolean = false;
  /** Read-only spectator embed (byte iframe): no player snake, no input,
   *  camera follows `spectateSnakeId` (or the arena leader). */
  private spectateMode: boolean = false;
  private spectateSnakeId: string | null = null;
  private spectateRing: Phaser.GameObjects.Arc | null = null;
  /** Followed agent's decision currently shown / last animated in the HUD. */
  private spectateShownDecisionId: string | null = null;
  private spectatePlayedResolvedId: string | null = null;
  /** Tracks the followed agent's life so we can announce its elimination once. */
  private spectateAgentSeenAlive: boolean = false;
  private spectateEliminatedShown: boolean = false;
  /** Tournament run — credit-gated, one life, separate score. No chest
   *  ceremony: the run ends on the first extraction (or death). */
  private tournamentMode: boolean = false;
  /** Server-authoritative tournament run result (set by 'tournament_result',
   *  shown when the death/extraction animation finishes). */
  private pendingTournamentResult: { runScore?: number; totalScore?: number; kills?: number; died?: boolean } | null = null;
  private gridWidth: number = GRID.width;
  private gridHeight: number = GRID.height;

  // Sprite pools
  private snakeSprites: Map<string, Phaser.GameObjects.Image[]> = new Map();
  private hatSprites: Map<string, Phaser.GameObjects.Image> = new Map();
  private nameLabels: Map<string, Phaser.GameObjects.Text> = new Map();
  private playerHatKey: string = '';  // loaded from skin preferences
  private coinSprites: Map<string, Phaser.GameObjects.Image> = new Map();
  private portalSprites: Map<string, Phaser.GameObjects.Sprite> = new Map();
  private portalGlows: Map<string, Phaser.GameObjects.Image> = new Map();
  private portalParticles: Map<string, Phaser.GameObjects.Particles.ParticleEmitter> = new Map();
  private knownActivePortals: Set<string> = new Set();
  private zoneGraphics: Phaser.GameObjects.Graphics | null = null;
  private wallGraphics: Phaser.GameObjects.Graphics | null = null;
  private gridGraphics: Phaser.GameObjects.Graphics | null = null;

  // Effects systems
  private juice!: JuiceEffects;
  private announcer!: AnnouncerOverlay;

  // Game state
  private gameStarted: boolean = false;
  private playerDead: boolean = false;
  private lastKnownHeadPos: { x: number; y: number } | null = null;

  // Latest state from server
  private latestSnakes: Record<string, SnakeData> = {};
  private latestCoins: Record<string, CoinData> = {};
  private latestZones: ZoneData[] = [];
  private latestTick: number = 0;
  private borderOffset: number = 0;

  // Frenzy zone overlay
  private frenzyZone: Phaser.GameObjects.Rectangle | null = null;
  // Frenzy state mirrored for the minimap (the game obj alone doesn't expose its grid coords)
  private frenzyState: { x: number; y: number; size: number } | null = null;

  // Cached desired zoom — lerped each frame so it doesn't snap when length changes
  private targetZoom: number = 2;

  // Crypto chart — tracks coin history for end-of-life display
  private coinChart: CoinChart = new CoinChart();

  // Interpolation state
  private previousSnakes: Record<string, { segments: { x: number; y: number }[] }> = {};
  // Per-snake timestamp of the last actual movement. Lets us interpolate
  // each snake over its own MOVE_TICK_MS window, separate from the 100ms
  // state-broadcast cadence (which fires even on no-move ticks).
  private snakeMoveTime: Map<string, number> = new Map();
  private lastStateTime: number = 0;
  /** Set once a drop/stall has been surfaced so we only report it once. */
  private connectionLost: boolean = false;
  /** No server state for this long (ms) while connected = dead feed. */
  private static readonly STALL_TIMEOUT_MS = 6000;
  /** performance.now() of the previous update() tick — detects a paused loop
   *  (backgrounded tab / heavy load) so it isn't mistaken for a network stall. */
  private lastUpdateAt: number = 0;
  private cameraX: number = 0;
  private cameraY: number = 0;
  private latencyTimer: Phaser.Time.TimerEvent | null = null;
  private lastLatencyMs: number | null = null;
  private latencyJitterMs: number = 0;
  private collisionAuditEnabled: boolean = false;
  private lastCollisionAuditWarningAt: number = 0;
  private pendingExtractionResults: ExtractionResult[] = [];

  constructor() {
    super({ key: 'GameScene' });
  }

  preload(): void {
    // Music. Tournament gets its own "peng" pool; the arena gets the sand
    // pool. Only the relevant set is downloaded (data is available here via
    // scene settings, before create()).
    const isTournament = !!(this.scene.settings.data as { tournamentMode?: boolean } | undefined)?.tournamentMode;
    if (isTournament) {
      for (const k of ['musicTournamentpeng1', 'musicTournamentpeng2', 'musicTournamentpeng3']) {
        if (!this.cache.audio.exists(k)) this.load.audio(k, `sounds/musc_tournamets/${k}.mp3`);
      }
    } else {
      if (!this.cache.audio.exists('arena_sand1')) {
        this.load.audio('arena_sand1', 'music/arena_sand1.mp3');
      }
      if (!this.cache.audio.exists('arena_sand2')) {
        this.load.audio('arena_sand2', 'music/arena_sand2.mp3');
      }
    }

    // SFX needed during a match
    loadSfxGroup(this, GAME_SFX);
    loadSfxGroup(this, CHEST_SFX);
    loadKillVoicelines(this);

    // Snake sprites — greyscale base (colored via gradient map shader)
    if (!this.textures.exists('snake_head')) {
      this.load.image('snake_head', 'assets/sprites/snake/snake_head_grey.png');
    }
    if (!this.textures.exists('snake_body')) {
      this.load.image('snake_body', 'assets/sprites/snake/snake_body_grey.png');
    }
    // Peng bot skin (tournament). Loaded always; only used when a snake's
    // skinId is 'peng', so the base game is unaffected.
    if (!this.textures.exists('peng_face')) {
      this.load.image('peng_face', 'assets/sprites/snake/peng_face.png');
    }
    if (!this.textures.exists('peng_body')) {
      this.load.image('peng_body', 'assets/sprites/snake/peng_body.png');
    }

    // Portal spritesheet (17 frames, 64x64 each)
    if (!this.textures.exists('portal')) {
      this.load.spritesheet('portal', 'assets/sprites/portals/portalsprite.png', {
        frameWidth: 64,
        frameHeight: 64,
      });
    }

    // Coin sprites
    if (!this.textures.exists('coin_common')) {
      this.load.image('coin_common', 'assets/sprites/coins/coin_common.png');
      this.load.image('coin_rare', 'assets/sprites/coins/coin_rare.png');
      this.load.image('coin_jackpot', 'assets/sprites/coins/coin_jackpot.png');
    }

    // Hat sprites — needed because remote players' chosen hats render here
    const hatNames = ['wizard', 'crown', 'cowboy', 'viking', 'party', 'cap', 'knight', 'pepper'];
    for (let i = 0; i < hatNames.length; i++) {
      const key = `hat_${hatNames[i]}`;
      if (this.textures.exists(key)) continue;
      this.load.image(key, `assets/sprites/hats/frame_${String(i + 1).padStart(3, '0')}.png`);
    }

    // Chest sprites — used by ChestCeremonyScene which has no preload of its own
    const tiers = ['salvage', 'field', 'case', 'vault', 'leviathan'];
    for (const tier of tiers) {
      const closed = `chest_${tier}_closed`;
      const open = `chest_${tier}_open`;
      if (!this.textures.exists(closed)) this.load.image(closed, `assets/sprites/chests/chest_${tier}_closed.png`);
      if (!this.textures.exists(open))   this.load.image(open,   `assets/sprites/chests/chest_${tier}_open.png`);
    }
  }

  async create(data?: { selectedPerk?: { id: string; category?: string }; entryToken?: string; demoMode?: boolean; tournamentMode?: boolean; tournamentTicket?: string | null; spectate?: boolean; spectateSnakeId?: string | null }): Promise<void> {
    disableMenuScroll();
    // Store perk for re-entry and color
    this._selectedPerk = data?.selectedPerk ?? null;
    this.playerCategory = data?.selectedPerk?.category ?? '';
    this.demoMode = !!data?.demoMode;
    this.tournamentMode = !!data?.tournamentMode;
    this.spectateMode = !!data?.spectate;
    this.spectateSnakeId = data?.spectateSnakeId ?? null;
    const urlParams = new URLSearchParams(window.location.search);
    this.collisionAuditEnabled = urlParams.has('collisionAudit') && urlParams.get('collisionAudit') !== '0';

    // Register the portal animation once the spritesheet is in cache.
    // (Was previously done in BootScene; moved here because BootScene no
    // longer preloads the spritesheet.)
    if (!this.anims.exists('portal_active') && this.textures.exists('portal')) {
      this.anims.create({
        key: 'portal_active',
        frames: this.anims.generateFrameNumbers('portal', { start: 0, end: 16 }),
        frameRate: 12,
        repeat: -1,
      });
    }

    // Load skin preferences
    this.loadSkinPreferences();

    // Connect to server
    this.client = new ColyseusClient();

    try {
      const perkId = data?.selectedPerk?.id ?? null;
      const joinData = this.spectateMode
        ? await this.client.joinAsSpectator()
        : this.tournamentMode
          ? await this.client.joinTournament(perkId, data?.tournamentTicket ?? null)
          : this.demoMode
            ? await this.client.joinDemo(perkId)
            : await this.client.joinSinglePlayer(perkId, data?.entryToken ?? null);
      // Observer has no snake of its own — leave mySnakeId empty so all the
      // player-specific branches (death overlay, extraction, combos) no-op.
      this.mySnakeId = joinData.snakeId ?? '';
      this.gridWidth = joinData.gridWidth;
      this.gridHeight = joinData.gridHeight;
      this.registry.set('gridWidth', this.gridWidth);
      this.registry.set('gridHeight', this.gridHeight);
      // Stake multiplier from the entry token (1/2/5/10). HUD,
      // coin-pickup floaters, and any other "score display" surface
      // reads this from the registry to render N× the raw value.
      // Underlying snake state stays unmultiplied so chest thresholds
      // (10/25/50/100) keep working.
      this.registry.set('stakeMultiplier', joinData.stakeMultiplier ?? 1);

      // Funnel event: a paid play actually started (post payment + onJoin
      // succeeded). Gap vs `play_button_clicked` = wallet/payment drop-off.
      // Demo joins fire `demo_match_joined` instead — they didn't pay so
      // the funnel math is different.
      if (this.spectateMode) {
        // Observer — no funnel tracking.
      } else if (this.demoMode) {
        track('demo_match_joined');
      } else {
        trackPaidPlayStarted({ stake: joinData.stakeMultiplier ?? 1 });
      }

      // Surface an unexpected socket drop/error instead of freezing silently.
      this.client.on('disconnected', (info: any) => this.handleConnectionLost(info));
    } catch (err) {
      console.error('Failed to connect to server:', err);
      // Spectator embed: don't bounce to the menu (that'd show chrome inside
      // the iframe) — just leave the empty board; the room is persistent so
      // this is rare and usually transient.
      if (this.spectateMode) return;
      track('match_join_failed', { demo: this.demoMode });
      this.scene.start('MainMenuScene');
      return;
    }

    // Setup camera
    // No camera bounds — always center on player, even near edges
    this.cameras.main.setZoom(2);
    // Spectator embed runs muted (autoplay in an iframe has no user gesture).
    if (this.spectateMode) this.sound.mute = true;
    this.gameStarted = false;
    this.playerDead = false;
    this.lastKnownHeadPos = null;

    // CRT post-process — applied to game camera only (HUD stays clean for
    // readability). Disable with ?crt=0 in the URL.
    const crtEnabled = !urlParams.has('crt') || urlParams.get('crt') !== '0';
    if (crtEnabled) {
      this.cameras.main.setPostPipeline('CRT');
      const pipeline = this.cameras.main.getPostPipeline('CRT');
      if (pipeline && !Array.isArray(pipeline)) {
        // Expose for live tuning from the browser console
        (window as unknown as { __crt: unknown }).__crt = pipeline;
      }
    }

    // Demo mode badge — fixed top-center, always visible during a demo run
    if (this.demoMode) {
      const { width } = this.scale;
      const badge = this.add.text(width / 2, 18, 'DEMO MODE — no real rewards', {
        fontFamily: 'monospace', fontSize: '12px', color: '#ffd166',
        stroke: '#000', strokeThickness: 3, fontStyle: 'bold',
      }).setOrigin(0.5, 0).setScrollFactor(0).setDepth(400).setAlpha(0.85);
      this.tweens.add({ targets: badge, alpha: 0.6, duration: 1800, yoyo: true, repeat: -1 });
    }

    // Tournament mode gets a cold "snowy night" backdrop (the playfield
    // palette is themed inside drawGrid/drawWalls/createAmbientEffects). Only
    // the camera background needs setting here; the arena is left untouched.
    if (this.tournamentMode) this.cameras.main.setBackgroundColor('#0a1622');

    // Draw static elements
    this.gridGraphics = this.add.graphics();
    this.wallGraphics = this.add.graphics();
    this.zoneGraphics = this.add.graphics();
    this.drawGrid();
    this.drawWalls();
    this.createAmbientEffects();

    // Init chart for this life
    this.coinChart = new CoinChart();
    this.coinChart.markBuy(0);

    // Start countdown — skipped when spectating (we join a match already in
    // progress, so there's no "get ready" moment; just render live).
    if (this.spectateMode) {
      this.gameStarted = true;
    } else {
      this.showCountdown();
    }

    // Setup effects systems
    this.juice = new JuiceEffects(this);
    this.announcer = new AnnouncerOverlay(this);

    // Setup input — skipped when spectating (read-only, no player to steer).
    if (!this.spectateMode) {
      this.inputManager = new InputManager(this, (dir) => {
        this.client.sendInput(dir);
      });
    }

    // Listen for state updates
    this.client.on('state', (data: any) => {
      const now = performance.now();
      // Snapshot previous segments only for snakes that ACTUALLY moved this
      // tick. Snakes move every 2 server ticks, but state broadcasts every
      // tick — if we reset on every broadcast, the lerp finishes in 100ms
      // and the sprite sits idle for 100ms (the "step-and-pause" feel).
      const newPrev: Record<string, { segments: { x: number; y: number }[] }> = {};
      const newSnakes = data.snakes;
      for (const [id, snake] of Object.entries(this.latestSnakes)) {
        const s = snake as SnakeData;
        const next = newSnakes[id];
        if (!s.isAlive || !next?.isAlive || s.segments.length === 0 || next.segments.length === 0) {
          continue;
        }
        const oldHead = s.segments[0];
        const newHead = next.segments[0];
        if (oldHead.x !== newHead.x || oldHead.y !== newHead.y) {
          // Snake moved — snapshot the position it's coming from
          newPrev[id] = { segments: s.segments.map(seg => ({ x: seg.x, y: seg.y })) };
          this.snakeMoveTime.set(id, now);
        } else {
          // No move — preserve existing prev so the in-flight lerp keeps going
          const existing = this.previousSnakes[id];
          if (existing) newPrev[id] = existing;
        }
      }
      this.previousSnakes = newPrev;
      this.lastStateTime = now;

      const previousMine = this.latestSnakes[this.mySnakeId];

      this.latestSnakes = data.snakes;
      this.latestCoins = data.coins;
      this.latestZones = data.extractionZones;
      this.latestTick = data.tick;

      // Sync the input manager to our snake's actual spawn direction.
      // Without this the client's stale 'RIGHT' default silently drops
      // valid turns during the spawn-invulnerability window ("ghost mode")
      // — feels like the controls are dead for the first second.
      const mine = data.snakes[this.mySnakeId] as any;
      const spawnedNow = !!mine?.isAlive && (!previousMine?.isAlive || previousMine.segments.length === 0);
      if (mine?.direction && (this.inputSyncedFor !== this.mySnakeId || spawnedNow)) {
        this.inputManager?.syncDirection(mine.direction);
        this.inputSyncedFor = this.mySnakeId;
      }
      if (data.borderOffset !== this.borderOffset) {
        this.borderOffset = data.borderOffset;
        this.drawWalls();
      }

      // Render immediately on network state arrival. Waiting until the next
      // Phaser update can leave a short but real window where latestSnakes
      // already contains a dangerous segment while no sprite exists for it.
      this.renderSnakes();

      // Record chart data every 5 ticks (2x per second)
      if (data.tick % 5 === 0) {
        const mySnake = data.snakes[this.mySnakeId];
        if (mySnake?.isAlive) {
          this.coinChart.addPoint(data.tick, mySnake.coins ?? 0, mySnake.totalBanked ?? 0);
        }
      }
    });

    this.startLatencyMeter();

    // Adaptive arena: server tells us when the physical grid grew.
    // Update local dims, repaint background + walls, and let the HUD
    // know so the minimap can re-anchor.
    this.client.on('grid_resized', (data: any) => {
      const w = Number(data?.width);
      const h = Number(data?.height);
      if (!Number.isFinite(w) || !Number.isFinite(h)) return;
      if (w === this.gridWidth && h === this.gridHeight) return;
      this.gridWidth = w;
      this.gridHeight = h;
      this.registry.set('gridWidth', w);
      this.registry.set('gridHeight', h);
      this.drawGrid();
      this.drawWalls();
      this.game.events.emit('gridResized', { width: w, height: h });
    });

    // Listen for game events
    this.client.on('snake_died', (data: any) => {
      if (data.snakeId === this.mySnakeId) {
        // Funnel: terminal event for the run. Banked stays raw (not
        // stake-multiplied) so we can compare death severity across
        // stake tiers fairly.
        const mySnakeForStats = this.latestSnakes[this.mySnakeId];
        const stake = (this.registry.get('stakeMultiplier') as number | undefined) ?? 1;
        const banked = mySnakeForStats?.totalBanked ?? 0;
        if (this.demoMode) {
          track('demo_died', { banked });
          trackDemoEnded('died');
        } else {
          track('match_died', { banked, stake });
        }
        sfx(this, 'player_death');
        // Get head world position for explosion effect
        const mySnake = this.latestSnakes[this.mySnakeId];
        const headX = mySnake?.segments?.[0] ? mySnake.segments[0].x * CELL_SIZE + CELL_SIZE / 2 : undefined;
        const headY = mySnake?.segments?.[0] ? mySnake.segments[0].y * CELL_SIZE + CELL_SIZE / 2 : undefined;
        this.juice.playerDeath(headX, headY);
        this.playerDead = true;
        this.showDeathDebug(data);

        // Chart: crash to 0
        this.coinChart.markDeath(this.latestTick);

        // Show death overlay after animation
        this.time.delayedCall(1500, () => {
          this.client?.disconnect();
          this.scene.stop('HUDScene');

          const mySnake = this.latestSnakes[this.mySnakeId];
          const coinsLost = mySnake?.coins ?? 0;
          const bankedLost = mySnake?.totalBanked ?? 0;
          const totalLost = coinsLost + bankedLost;

          if (this.tournamentMode) {
            // One life: death ends the run with no score.
            this.showTournamentResultOverlay(this.pendingTournamentResult ?? { runScore: 0, died: true });
          } else if (this.demoMode) {
            this.showDemoEndOverlay({ coinsBanked: bankedLost, kind: 'died' });
          } else {
            this.showDeathOverlay(totalLost, coinsLost, bankedLost);
          }
        });
      } else {
        // Any other snake death — show explosion at their position
        const pos = data.position;
        if (pos) {
          const wx = pos.x * CELL_SIZE + CELL_SIZE / 2;
          const wy = pos.y * CELL_SIZE + CELL_SIZE / 2;
          if (data.killedBy === this.mySnakeId) {
            // We killed them — louder feedback
            sfx(this, 'kill_hit');
            this.juice.killEffect(wx, wy, data.snakeId);
          } else {
            // Someone else died — smaller explosion
            sfx(this, 'bot_death', { volume: 0.4 });
            this.juice.otherSnakeDeath(wx, wy);
          }
        }
      }
    });

    this.client.on('coin_collected', (data: any) => {
      if (data.snakeId === this.mySnakeId) {
        const chainLvl = data.chainLevel ?? 1;
        if (chainLvl >= 5) sfx(this, 'coin_pickup_jackpot');
        else if (chainLvl >= 3) sfx(this, 'coin_pickup_chain');
        else sfx(this, 'coin_pickup');

        const mySnake = this.latestSnakes[this.mySnakeId];
        if (mySnake?.segments?.length > 0) {
          const head = mySnake.segments[0];
          const stake = (this.registry.get('stakeMultiplier') as number | undefined) ?? 1;
          this.juice.coinPickup(
            head.x * CELL_SIZE + CELL_SIZE / 2,
            head.y * CELL_SIZE,
            data.effectiveValue ?? data.value,
            data.chainLevel ?? 1,
            stake
          );
        }
      }
    });

    this.client.on('chain_update', (data: any) => {
      if (data.snakeId === this.mySnakeId) {
        sfx(this, 'chain_levelup');
        this.juice.chainUpdate(data.chainLevel, data.multiplier);
        // Chain combo announcer at thresholds
        if (data.chainLevel >= 3) {
          this.juice.chainAnnounce(data.chainLevel);
        }
      }
    });

    // Near-miss feedback when a good chain breaks
    this.client.on('chain_break', (data: any) => {
      if (data.snakeId === this.mySnakeId && data.maxChainReached >= 3) {
        this.juice.chainBreakFeedback(data.maxChainReached);
      }
    });

    // Coin frenzy zone events
    this.client.on('frenzy_start', (data: any) => {
      sfx(this, 'title_slam');
      this.announcer.announce('COIN FRENZY!', 'extraction');
      const x = data.position.x * CELL_SIZE + (data.size * CELL_SIZE) / 2;
      const y = data.position.y * CELL_SIZE + (data.size * CELL_SIZE) / 2;
      const w = data.size * CELL_SIZE;
      this.frenzyState = {
        x: data.position.x + data.size / 2,
        y: data.position.y + data.size / 2,
        size: data.size,
      };
      this.frenzyZone = this.add.rectangle(x, y, w, w, 0xFFDD00, 0.12)
        .setDepth(2).setStrokeStyle(1, 0xFFDD00, 0.4);
      this.tweens.add({
        targets: this.frenzyZone,
        alpha: { from: 0.08, to: 0.2 },
        duration: 500,
        yoyo: true,
        repeat: -1,
      });
    });

    this.client.on('frenzy_end', () => {
      this.frenzyZone?.destroy();
      this.frenzyZone = null;
      this.frenzyState = null;
    });

    // Tournament: server-authoritative run result. Stashed and shown once
    // the death/extraction animation finishes (one life, no chest ceremony).
    this.client.on('tournament_result', (data: any) => {
      this.pendingTournamentResult = data;
    });

    this.client.on('extraction_result', (data: any) => {
      if (data?.snakeId === this.mySnakeId && data.result) {
        // Stash the chest payout info alongside the result so the
        // ceremony can render SNK amounts (USD-stable, but shown in the
        // token the player actually receives).
        const enriched = { ...data.result, chestPayout: data.chestPayout ?? null };
        this.pendingExtractionResults.push(enriched as ExtractionResult);
      }
    });

    // Track extractions locally since server state may lag behind events
    let playerExtractions = 0;

    this.client.on('extraction', (data: any) => {
      if (data.snakeId === this.mySnakeId) {
        playerExtractions++;
        // Funnel: completion event. Captures stake from registry so we
        // can analyze "x10 players extract X% vs x1 players".
        const stake = (this.registry.get('stakeMultiplier') as number | undefined) ?? 1;
        if (this.demoMode) {
          track('demo_extracted', { coins_banked: data.coinsBanked, extraction_n: playerExtractions });
          trackDemoEnded('extracted');
        } else {
          track('match_extracted', { coins_banked: data.coinsBanked, extraction_n: playerExtractions, stake });
        }
        sfx(this, 'extraction_flash');
        sfx(this, 'portal_enter');
        this.juice.extractionEffect(data.coinsBanked);

        // Chart: mark SELL at extraction
        this.coinChart.markSell(this.latestTick, data.coinsBanked);

        this.time.delayedCall(2000, () => {
          this.scene.stop('HUDScene');

          // Tournament: extraction IS the result. One life, no chest
          // ceremony — show the run result and head back to the menu.
          if (this.tournamentMode) {
            this.client?.disconnect();
            this.showTournamentResultOverlay(
              this.pendingTournamentResult ?? { runScore: data.coinsBanked, died: false },
            );
            return;
          }

          // Demo mode: skip the chest ceremony entirely (no rewards to claim).
          // On the final extraction, route to the demo end CTA. On 1st/2nd,
          // immediately resume play after a brief pause so they can re-enter.
          if (this.demoMode) {
            if (playerExtractions < 3) {
              // Auto re-enter — no ceremony, just keep playing
              this.scene.launch('HUDScene', { demoMode: true });
              this.client?.sendReenter();
            } else {
              this.client?.disconnect();
              this.showDemoEndOverlay({ coinsBanked: data.coinsBanked, kind: 'extracted' });
            }
            return;
          }

          if (playerExtractions < 3) {
            const serverResult = this.pendingExtractionResults.shift();
            // Partial extraction — hide game, show ceremony, keep connection
            this.scene.setVisible(false, 'GameScene');
            this.scene.pause();
            this.scene.launch('ChestCeremonyScene', {
              result: serverResult,
              coinsBanked: data.coinsBanked,
              coinsCollected: data.coinsBanked,
              kills: 0,
              chainMax: 0,
              perkUsed: this.playerCategory ?? null,
              extractionsUsed: playerExtractions,
              selectedPerk: this._selectedPerk,
              returnToGame: true,
            });
          } else {
            const serverResult = this.pendingExtractionResults.shift();
            // Final extraction (3rd) — disconnect and go to ceremony
            this.client?.disconnect();
            this.scene.start('ChestCeremonyScene', {
              result: serverResult,
              coinsBanked: data.coinsBanked,
              coinsCollected: data.coinsBanked,
              kills: 0,
              chainMax: 0,
              perkUsed: this.playerCategory ?? null,
              extractionsUsed: playerExtractions,
              selectedPerk: this._selectedPerk,
              returnToGame: false,
              coinChart: this.coinChart,
            });
          }
        });
      }
    });

    this.client.on('match_end', (data: any) => {
      sfx(this, 'match_end');
      this.scene.stop('HUDScene');
      // Disconnect from server — match is over
      this.client?.disconnect();
      this.scene.start('GameOverScene', {
        leaderboard: data.leaderboard,
        mySnakeId: this.mySnakeId,
      });
    });

    // Kill streak announcer
    this.client.on('kill_streak', (data: any) => {
      this.announcer.announceKillStreak(
        data.announcerMessage, data.playerName, data.killCount, data.bonusCoins
      );
    });

    // Generic announcer (first blood, shutdown)
    this.client.on('announcer', (data: any) => {
      this.announcer.announce(data.message, data.style);
    });

    // Kill feed forwarding to HUD
    this.client.on('kill_feed', (data: any) => {
      this.game.events.emit('killFeed', data);
    });

    // Listen for re-entry requests from ChestCeremonyScene
    this.game.events.on('requestReenter', () => {
      this.client?.sendReenter();
    });
    this.game.events.on('requestCancelReentry', () => {
      this.client?.sendCancelReentry();
      this.client?.disconnect();
    });

    // Start HUD as parallel scene
    this.scene.launch('HUDScene', { demoMode: this.demoMode, spectate: this.spectateMode });

    // Background music — random pick from the 'arena' pool, auto-rotates
    // through the rest when each track ends. Intentionally NOT stopped on
    // GameScene shutdown so the track keeps playing through ChestCeremony
    // and GameOver — MainMenuScene calls stopMusic() when you actually
    // leave the arena loop.
    if (!this.spectateMode) playMusic(this.game, this.tournamentMode ? 'tournament' : 'arena');

    // Emit shared events for HUD. When spectating, report the FOLLOWED snake's
    // stats so the HUD (coins/length/etc.) reflects the agent on screen.
    this.game.events.on('requestGameData', (callback: Function) => {
      const hudSnakeId = this.spectateMode ? this.cameraTargetId() : this.mySnakeId;
      const mySnake = this.latestSnakes[hudSnakeId];
      callback({
        tick: this.latestTick,
        coins: mySnake?.coins ?? 0,
        totalBanked: mySnake?.totalBanked ?? 0,
        extractionsUsed: mySnake?.extractionsUsed ?? 0,
        isAlive: mySnake?.isAlive ?? false,
        snakeLength: mySnake?.segments?.length ?? 0,
        perkName: (data as any)?.selectedPerk?.name ?? '',
      });
    });

    // Live arena leaderboard with rejoin-accumulation. Score per PLAYER:
    //   bankedFromPastLives + (currentLife: coins + totalBanked)
    // When a life ends, its totalBanked is rolled into bankedFromPastLives
    // so a rejoin starts from that base instead of zero.
    this.game.events.on('requestLeaderboardData', (callback: Function) => {
      // Index current snakes by display name. If somehow two snakes share
      // a name (rare), prefer the alive one as the "current life".
      const snakesByName = new Map<string, any>();
      for (const s of Object.values(this.latestSnakes) as any[]) {
        const name = s.playerId;
        const existing = snakesByName.get(name);
        if (!existing || (s.isAlive && !existing.isAlive)) {
          snakesByName.set(name, s);
        }
      }

      // Reconcile each broadcast snake against its player history
      for (const [name, s] of snakesByName) {
        let h = this.leaderboardHistory.get(name);
        if (!h) {
          h = { name, isBot: !!s.isBot, bankedFromPastLives: 0, currentSnake: null, stakeMultiplier: 1 };
          this.leaderboardHistory.set(name, h);
        }
        // New snake instance for this player → previous life's bank rolls
        // into the accumulated past-lives total
        if (h.currentSnake && h.currentSnake.id !== s.id) {
          h.bankedFromPastLives += h.currentSnake.banked;
          h.currentSnake = null;
        }
        h.currentSnake = {
          id: s.id,
          coins: s.coins ?? 0,
          banked: s.totalBanked ?? 0,
          alive: !!s.isAlive,
        };
        // Promote the recorded stake when a higher one appears (mirrors
        // the server's GREATEST semantic in arena_participation). Stays
        // at the max across lives — matches the badge displayed on the
        // arena toast and on the in-game LB rendering.
        const broadcastStake = typeof s.stakeMultiplier === 'number' && s.stakeMultiplier > 0
          ? s.stakeMultiplier
          : 1;
        if (broadcastStake > h.stakeMultiplier) h.stakeMultiplier = broadcastStake;
      }

      // Snakes no longer in broadcast: their life ended (death + cleanup
      // or disconnect). Roll their banked into the past-lives total once.
      for (const h of this.leaderboardHistory.values()) {
        if (h.currentSnake && !snakesByName.has(h.name)) {
          h.bankedFromPastLives += h.currentSnake.banked;
          h.currentSnake = null;
        }
      }

      // Identify "me" by display name (snake.id changes across rejoins,
      // playerId/displayName stays stable).
      const myName = (this.latestSnakes[this.mySnakeId] as any)?.playerId ?? null;

      const entries = [...this.leaderboardHistory.values()]
        .map(h => {
          const liveScore = h.currentSnake
            ? h.currentSnake.coins + h.currentSnake.banked
            : 0;
          const raw = h.bankedFromPastLives + liveScore;
          // Multiply by stake so the in-game LB reflects pool-share
          // weight, not just raw coins banked. A x10 player with 6
          // raw banked ranks above a x1 player with 50 (60 > 50).
          return {
            id: h.currentSnake?.id ?? h.name,
            name: h.name,
            score: raw * h.stakeMultiplier,
            isBot: h.isBot,
            isMe: myName !== null && h.name === myName,
            alive: !!h.currentSnake?.alive,
          };
        })
        .filter(e => e.score > 0 || e.isMe)
        .sort((a, b) => b.score - a.score);
      callback({ entries, mySnakeId: this.mySnakeId });
    });
  }

  update(): void {
    if (!this.scene.isActive()) return;
    // Stall watchdog: the server broadcasts state several times a second, so
    // a multi-second gap (while still "connected") means the feed died —
    // the snakes-freeze symptom. Surface it instead of sitting frozen.
    //
    // NOTE: lastStateTime uses performance.now() — compare against the SAME
    // clock (not Date.now()).
    //
    // FALSE-POSITIVE GUARD: requestAnimationFrame is paused while the tab is
    // backgrounded (and stalls under heavy load), so the loop can be dormant
    // for tens of seconds. On resume, a single huge frame delta would make a
    // benign gap look like a "stall" (we saw 46s gaps from a 6s threshold).
    // If the loop just woke from a >2s pause, it wasn't a network stall —
    // give a fresh grace window instead of firing. Also: only watch during
    // actual gameplay, never during the load/countdown.
    const nowP = performance.now();
    const frameDelta = this.lastUpdateAt > 0 ? nowP - this.lastUpdateAt : 0;
    this.lastUpdateAt = nowP;
    if (frameDelta > 2000) {
      this.lastStateTime = nowP;
    } else if (
      !this.connectionLost &&
      this.gameStarted &&
      this.lastStateTime > 0 &&
      this.client?.isConnected() &&
      nowP - this.lastStateTime > GameScene.STALL_TIMEOUT_MS
    ) {
      this.handleConnectionLost({ reason: 'stall', sinceMs: nowP - this.lastStateTime });
    }
    this.inputManager?.update();
    this.renderSnakes();
    if (this.collisionAuditEnabled) this.auditNearbyCollisionVisibility();
    this.renderCoins();
    this.renderZones();
    this.followPlayerSnake();
    this.updateDynamicZoom();
    this.updateMiniMap();
    if (this.spectateMode) this.updateSpectateDecision();
  }

  /** Spectator: mirror the followed agent's held decision to the HUD. When the
   *  agent is picking a perk, show the card overlay; when it resolves, animate
   *  the chosen card / play the chest reveal. Driven entirely by the agent's
   *  state (pendingDecision / lastResolved) — the spectator chooses nothing. */
  private updateSpectateDecision(): void {
    // Announce the followed agent's elimination once (instead of silently
    // jumping to the leader). Only for the specific agentId we were asked to
    // follow — extraction (isExtracted) is a win, not an elimination.
    if (this.spectateSnakeId) {
      const self = this.latestSnakes[this.spectateSnakeId];
      if (self?.isAlive) {
        this.spectateAgentSeenAlive = true;
      } else if (this.spectateAgentSeenAlive && !this.spectateEliminatedShown && (!self || !self.isExtracted)) {
        this.spectateEliminatedShown = true;
        this.game.events.emit('spectate:eliminated');
      }
    }

    const snake = this.latestSnakes[this.cameraTargetId()] as unknown as {
      pendingDecision?: { id: string; kind: string; options: { id: string; name: string; description?: string; rarity?: string }[] } | null;
      lastResolved?: { id: string; kind: string; chosenOptionId: string; chestResult?: unknown } | null;
    } | undefined;
    const pd = snake?.pendingDecision ?? null;
    const lr = snake?.lastResolved ?? null;

    // A newly-resolved decision → animate it once.
    if (lr && lr.id !== this.spectatePlayedResolvedId) {
      this.spectatePlayedResolvedId = lr.id;
      this.spectateShownDecisionId = null;
      if (lr.kind === 'perk') {
        this.game.events.emit('spectate:perkChosen', { chosenOptionId: lr.chosenOptionId });
      } else if (lr.kind === 'chest') {
        this.game.events.emit('spectate:chestReveal', { chestResult: lr.chestResult });
      }
      return;
    }

    // A newly-opened perk decision → show the "AGENT IS CHOOSING…" cards.
    if (pd && pd.id !== this.spectateShownDecisionId) {
      this.spectateShownDecisionId = pd.id;
      if (pd.kind === 'perk') {
        this.game.events.emit('spectate:perkOpen', { options: pd.options });
      }
      // A pending chest shows nothing until it's opened (then the reveal fires).
      return;
    }

    // Pending cleared with no resolution we caught → close any open overlay.
    if (!pd && !lr && this.spectateShownDecisionId) {
      this.spectateShownDecisionId = null;
      this.game.events.emit('spectate:decisionClose');
    }
  }

  private showDeathDebug(data: any): void {
    const killedBy = typeof data?.killedBy === 'string' ? data.killedBy : null;
    const collidedWith = Array.isArray(data?.collidedWith) ? data.collidedWith as string[] : [];
    const killer = killedBy ? this.latestSnakes[killedBy] : null;
    const collisionNames = collidedWith
      .map((id) => this.latestSnakes[id]?.playerId ?? id)
      .join(',');
    const killerName = data?.snakeId === killedBy
      ? 'self'
      : (killer?.playerId ?? killedBy ?? collisionNames) || 'none';
    const pos = data?.position;
    const posText = pos ? `${pos.x},${pos.y}` : 'unknown';
    const cause = data?.snakeId === killedBy ? 'SELF_COLLISION' : data?.cause ?? 'UNKNOWN';
    const line = `DEATH ${cause} @ ${posText} by ${killerName}`;

    console.warn('[death-debug]', {
      cause: data?.cause,
      position: data?.position,
      killedBy,
      collidedWith,
      participants: data?.participants,
      killer,
      tick: this.latestTick,
      mySnake: this.latestSnakes[this.mySnakeId],
    });
    this.auditDeathVisibility(data);

    this.add.text(this.scale.width / 2, 58, line, {
      fontFamily: 'monospace',
      fontSize: '12px',
      color: '#ffdd88',
      stroke: '#000000',
      strokeThickness: 4,
      fontStyle: 'bold',
    }).setOrigin(0.5, 0).setScrollFactor(0).setDepth(520);
  }

  private isNearPlayerHead(pos: { x: number; y: number }, radius: number): boolean {
    const player = this.latestSnakes[this.mySnakeId];
    const head = player?.segments?.[0];
    if (!player?.isAlive || !head) return false;
    return Math.abs(pos.x - head.x) + Math.abs(pos.y - head.y) <= radius;
  }

  private cellCenter(pos: { x: number; y: number }): { x: number; y: number } {
    return {
      x: pos.x * CELL_SIZE + CELL_SIZE / 2,
      y: pos.y * CELL_SIZE + CELL_SIZE / 2,
    };
  }

  private segmentRenderAudit(snakeId: string, segmentIndex: number, segment: { x: number; y: number }) {
    const expected = this.cellCenter(segment);
    const sprite = this.snakeSprites.get(snakeId)?.[segmentIndex] ?? null;
    const actual = sprite?.scene ? { x: sprite.x, y: sprite.y } : null;
    const distancePx = actual ? Math.hypot(actual.x - expected.x, actual.y - expected.y) : Infinity;

    return {
      snakeId,
      playerId: this.latestSnakes[snakeId]?.playerId ?? snakeId,
      segmentIndex,
      segment,
      expected,
      actual,
      distancePx: Number.isFinite(distancePx) ? Math.round(distancePx * 10) / 10 : null,
      visible: !!actual,
      aligned: !!actual && distancePx <= CELL_SIZE * 0.55,
    };
  }

  private auditSnapshotSegment(
    snakeId: string,
    segmentIndex: number,
    segment: { x: number; y: number },
    snapshotLabel: string
  ) {
    const audit = this.segmentRenderAudit(snakeId, segmentIndex, segment);
    return {
      ...audit,
      snapshot: snapshotLabel,
      localSegment: this.latestSnakes[snakeId]?.segments?.[segmentIndex] ?? null,
    };
  }

  private auditDeathVisibility(data: any): void {
    const deathPos = data?.position;
    if (!deathPos) return;

    const participants = Array.isArray(data?.participants)
      ? data.participants as DeathParticipantData[]
      : [];
    const candidates = new Set<string>();
    if (typeof data?.killedBy === 'string') candidates.add(data.killedBy);
    if (Array.isArray(data?.collidedWith)) {
      for (const id of data.collidedWith) {
        if (typeof id === 'string') candidates.add(id);
      }
    }
    candidates.delete(this.mySnakeId);

    if (candidates.size === 0) {
      for (const [id, snake] of Object.entries(this.latestSnakes)) {
        if (id === this.mySnakeId || !snake.isAlive) continue;
        if (snake.segments.some(seg => seg.x === deathPos.x && seg.y === deathPos.y)) {
          candidates.add(id);
        }
      }
    }

    const authoritativeAudits = participants.flatMap((participant) => {
      if (!candidates.has(participant.id)) return [];
      return participant.segments
        .map((segment, segmentIndex) => ({ segment, segmentIndex }))
        .filter(({ segment }) => segment.x === deathPos.x && segment.y === deathPos.y)
        .map(({ segment, segmentIndex }) =>
          this.auditSnapshotSegment(participant.id, segmentIndex, segment, 'death-event')
        );
    });

    const localAudits = [...candidates].flatMap((id) => {
      const snake = this.latestSnakes[id];
      if (!snake) return [];
      return snake.segments
        .map((segment, segmentIndex) => ({ segment, segmentIndex }))
        .filter(({ segment }) => segment.x === deathPos.x && segment.y === deathPos.y)
        .map(({ segment, segmentIndex }) =>
          this.auditSnapshotSegment(id, segmentIndex, segment, 'latest-client-state')
        );
    });
    const audits = authoritativeAudits.length > 0 ? authoritativeAudits : localAudits;

    const participantHeads = participants.map((participant) => ({
      id: participant.id,
      playerId: participant.playerId,
      head: participant.segments[0] ?? null,
      latestClientHead: this.latestSnakes[participant.id]?.segments?.[0] ?? null,
    }));

    console.warn('[death-visibility-audit]', {
      cause: data?.cause,
      deathPos,
      tick: this.latestTick,
      participantHeads,
      localDeathCellAudits: localAudits,
      camera: {
        scrollX: Math.round(this.cameras.main.scrollX),
        scrollY: Math.round(this.cameras.main.scrollY),
        zoom: Math.round(this.cameras.main.zoom * 100) / 100,
      },
      audits,
      verdict: audits.length === 0
        ? 'no-rendered-candidate-for-death-cell'
        : audits.every(a => a.aligned)
          ? 'rendered-at-authoritative-cell'
          : 'render-mismatch-near-death-cell',
    });
  }

  private auditNearbyCollisionVisibility(): void {
    const now = performance.now();
    if (now - this.lastCollisionAuditWarningAt < 500) return;

    const player = this.latestSnakes[this.mySnakeId];
    const playerHead = player?.segments?.[0];
    if (!player?.isAlive || !playerHead) return;

    const issues: Array<ReturnType<GameScene['segmentRenderAudit']>> = [];
    for (const [id, snake] of Object.entries(this.latestSnakes)) {
      if (id === this.mySnakeId || !snake.isAlive) continue;

      for (let segmentIndex = 0; segmentIndex < snake.segments.length; segmentIndex++) {
        const segment = snake.segments[segmentIndex];
        const distanceCells = Math.abs(segment.x - playerHead.x) + Math.abs(segment.y - playerHead.y);
        if (distanceCells > 2) continue;

        const audit = this.segmentRenderAudit(id, segmentIndex, segment);
        if (!audit.aligned) issues.push(audit);
        if (issues.length >= 8) break;
      }
      if (issues.length >= 8) break;
    }

    if (issues.length === 0) return;

    this.lastCollisionAuditWarningAt = now;
    console.warn('[collision-audit]', {
      tick: this.latestTick,
      playerHead,
      issues,
    });
  }

  /**
   * Zoom smoothly from 2.0x (length 3) down to 1.55x (length 15+). Longer
   * snakes see more of the arena — compensates for the added blind spots
   * a long body creates. Easier for humans, invisible to agents.
   */
  private updateDynamicZoom(): void {
    const mySnake = this.latestSnakes[this.spectateMode ? this.cameraTargetId() : this.mySnakeId];
    if (mySnake?.isAlive) {
      const len = mySnake.segments.length;
      // Lerp between bounds based on length (3 -> 15 maps to 2.0 -> 1.55)
      const t = Math.min(1, Math.max(0, (len - 3) / 12));
      this.targetZoom = 2.0 - t * 0.45;
    }
    const cam = this.cameras.main;
    const next = cam.zoom + (this.targetZoom - cam.zoom) * 0.08;
    cam.setZoom(next);
  }

  /**
   * Push current radar data to HUDScene via game-bus event. The radar lives
   * in HUDScene (zoom 1 camera) so it isn't scaled by GameScene's zoom 2x.
   */
  private updateMiniMap(): void {
    const enemies: { x: number; y: number; color: number }[] = [];
    let player: { x: number; y: number; color: number } | undefined;

    const focusId = this.spectateMode ? this.cameraTargetId() : this.mySnakeId;
    for (const [id, snake] of Object.entries(this.latestSnakes)) {
      const s = snake as SnakeData;
      if (!s.isAlive || s.segments.length === 0) continue;
      const head = s.segments[0];
      if (id === focusId) {
        player = { x: head.x, y: head.y, color: 0xffffff };
      } else {
        enemies.push({ x: head.x, y: head.y, color: s.isBot ? 0xff6644 : 0x66aaff });
      }
    }

    const portals = this.latestZones
      .filter((z) => z.isActive)
      .map((z) => ({ x: z.position.x + 1, y: z.position.y + 1, color: 0x00ff88 }));

    this.game.events.emit('minimapData', {
      player,
      enemies,
      portals,
      frenzy: this.frenzyState ?? undefined,
    });
  }

  /** Snake color palettes — tint-based, head brighter than body */
  private static SNAKE_COLORS: { head: number; body: number }[] = [
    { head: 0x44DDFF, body: 0x0099DD }, // Blue (player)
    { head: 0x55FF33, body: 0x22CC00 }, // Viper Green
    { head: 0xFF4444, body: 0xDD1111 }, // Ember Red
    { head: 0xFF8822, body: 0xDD5500 }, // Voltage Orange
    { head: 0xBB55FF, body: 0x8822DD }, // Royal Purple
    { head: 0xFF55BB, body: 0xDD2288 }, // Hot Pink
    { head: 0x33FFDD, body: 0x00DDAA }, // Laser Teal
    { head: 0xEEFF33, body: 0xBBCC00 }, // Toxic Yellow
  ];

  /** Map perk category to player snake color */
  private static CATEGORY_SNAKE_COLORS: Record<string, { head: number; body: number }> = {
    ECONOMY: { head: 0xFFDD00, body: 0xCC9900 },     // Gold
    PVP:     { head: 0xFF4444, body: 0xCC1111 },      // Red
    MOVEMENT:{ head: 0x44DDFF, body: 0x0099DD },      // Cyan
    HIGH_RISK:{ head: 0xFF55FF, body: 0xCC22CC },     // Magenta
  };

  private getSnakeColor(snakeId: string, isPlayer: boolean): { head: number; body: number } {
    if (isPlayer) {
      // Color determined by perk category
      if (this.playerCategory) {
        return GameScene.CATEGORY_SNAKE_COLORS[this.playerCategory] ?? GameScene.SNAKE_COLORS[0];
      }
      return GameScene.SNAKE_COLORS[0];
    }
    let hash = 0;
    for (let i = 0; i < snakeId.length; i++) hash = (hash * 31 + snakeId.charCodeAt(i)) | 0;
    const idx = 1 + (Math.abs(hash) % (GameScene.SNAKE_COLORS.length - 1));
    return GameScene.SNAKE_COLORS[idx];
  }

  /** Get rotation angle from one segment to another */
  private getSegmentAngle(from: { x: number; y: number }, to: { x: number; y: number }): number {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    if (dx > 0) return 0;    // RIGHT
    if (dx < 0) return 180;  // LEFT
    if (dy > 0) return 90;   // DOWN
    if (dy < 0) return 270;  // UP
    return 0;
  }

  private getLocalMoveVisualMs(): number {
    if (this.lastLatencyMs === null || this.lastLatencyMs <= 60) {
      return LOCAL_SMOOTH_MOVE_MS;
    }
    if (this.lastLatencyMs >= 160) {
      return LOCAL_RESPONSIVE_MOVE_MS;
    }

    const t = (this.lastLatencyMs - 60) / 100;
    return Phaser.Math.Linear(LOCAL_SMOOTH_MOVE_MS, LOCAL_RESPONSIVE_MOVE_MS, t);
  }

  private getRemoteMoveVisualMs(): number {
    if (this.lastLatencyMs === null || this.lastLatencyMs <= 80) {
      return REMOTE_SMOOTH_MOVE_MS;
    }
    if (this.lastLatencyMs >= 180) {
      return REMOTE_CATCHUP_MOVE_MS;
    }

    const t = (this.lastLatencyMs - 80) / 100;
    return Phaser.Math.Linear(REMOTE_SMOOTH_MOVE_MS, REMOTE_CATCHUP_MOVE_MS, t);
  }

  private renderSnakes(): void {
    const spriteScale = CELL_SIZE / 32;
    const now = performance.now();

    // Clear old sprites not in current state
    for (const [id, sprites] of this.snakeSprites) {
      if (!this.latestSnakes[id]) {
        sprites.forEach(s => s.destroy());
        this.snakeSprites.delete(id);
        this.hatSprites.get(id)?.destroy();
        this.hatSprites.delete(id);
        this.nameLabels.get(id)?.destroy();
        this.nameLabels.delete(id);
        this.snakeMoveTime.delete(id);
      }
    }

    for (const [id, snake] of Object.entries(this.latestSnakes)) {
      if (!snake.isAlive) {
        const existing = this.snakeSprites.get(id);
        if (existing) {
          existing.forEach(s => s.destroy());
          this.snakeSprites.delete(id);
        }
        this.hatSprites.get(id)?.destroy();
        this.hatSprites.delete(id);
        this.nameLabels.get(id)?.destroy();
        this.nameLabels.delete(id);
        continue;
      }

      const isPlayer = id === this.mySnakeId;
      const segs = snake.segments;
      const prevData = this.previousSnakes[id];
      const colors = this.getSnakeColor(id, isPlayer);
      // Tournament peng bot — render with its own sprites, untinted.
      const isPeng = (snake as any).skinId === 'peng';

      // Per-snake interpolation factor — uses the timestamp of this snake's
      // last actual move, not the (more frequent) state-broadcast time.
      const moveAt = this.snakeMoveTime.get(id);
      const visualMoveMs = isPlayer ? this.getLocalMoveVisualMs() : this.getRemoteMoveVisualMs();
      const t = moveAt != null ? Math.min((now - moveAt) / visualMoveMs, 1) : 1;

      let sprites = this.snakeSprites.get(id);

      // Keep sprite pools in sync incrementally. Destroy/rebuild on every
      // length change creates one-frame invisible segments around growth.
      if (!sprites) {
        sprites = [];
        this.snakeSprites.set(id, sprites);
      }
      while (sprites.length < segs.length) {
        sprites.push(
          this.add.image(0, 0, 'snake_body')
            .setOrigin(0.5, 0.5)
            .setScale(spriteScale)
            .setDepth(isPlayer ? 10 : 5)
        );
      }
      while (sprites.length > segs.length) {
        const sprite = sprites.pop();
        sprite?.destroy();
      }
      for (let i = 0; i < segs.length; i++) {
        if (sprites[i]?.scene) continue;
        sprites[i]?.destroy();
        sprites[i] = this.add.image(0, 0, 'snake_body')
          .setOrigin(0.5, 0.5)
          .setScale(spriteScale)
          .setDepth(isPlayer ? 10 : 5);
      }

      // Update each segment with interpolation
      for (let i = 0; i < segs.length; i++) {
        const seg = segs[i];
        const sprite = sprites[i];

        // Interpolate position between previous and current
        let drawX: number;
        let drawY: number;

        const forceAuthoritativeCell = this.collisionAuditEnabled && !isPlayer && this.isNearPlayerHead(seg, 2);
        if (!forceAuthoritativeCell && prevData && i < prevData.segments.length && t < 1) {
          const prev = prevData.segments[i];
          drawX = (prev.x + (seg.x - prev.x) * t) * CELL_SIZE + CELL_SIZE / 2;
          drawY = (prev.y + (seg.y - prev.y) * t) * CELL_SIZE + CELL_SIZE / 2;
        } else {
          drawX = seg.x * CELL_SIZE + CELL_SIZE / 2;
          drawY = seg.y * CELL_SIZE + CELL_SIZE / 2;
        }

        if (i === 0) {
          sprite.setPosition(drawX, drawY);
          if (isPeng) {
            sprite.setTexture('peng_face');
            sprite.clearTint();
          } else {
            sprite.setTexture('snake_head');
            sprite.setTint(colors.head);
          }
          const angle = segs.length > 1
            ? this.getSegmentAngle(segs[1], segs[0])
            : 0;
          // Peng rotates with movement like the snake head, but its beak
          // points DOWN in the PNG (the snake head points RIGHT at angle 0),
          // so offset by -90 to align the beak with the travel direction.
          sprite.setAngle(isPeng ? angle - 90 : angle);
        } else {
          // Body segment — apply perpendicular slither offset.
          // Local tangent uses the neighbours so corners stay smooth:
          // segs[i-1] is ahead, segs[i+1] is behind (or fall back at the tail).
          const ahead = segs[i - 1];
          const behind = segs[i + 1] ?? seg;
          let tx = ahead.x - behind.x;
          let ty = ahead.y - behind.y;
          const tlen = Math.hypot(tx, ty) || 1;
          tx /= tlen;
          ty /= tlen;
          // Perpendicular = rotate tangent 90° (-ty, tx)
          const phase = this.time.now * SLITHER_FREQ - i * SEGMENT_PHASE_STEP;
          const wave = Math.sin(phase) * SLITHER_AMP;
          const slither = forceAuthoritativeCell ? 0 : wave;
          sprite.setPosition(drawX + (-ty) * slither, drawY + tx * slither);
          if (isPeng) {
            sprite.setTexture('peng_body');
            sprite.clearTint();
            sprite.setScale(spriteScale * 0.9); // peng body 10% smaller
          } else {
            sprite.setTexture('snake_body');
            sprite.setTint(colors.body);
          }
          // Body sprite is a directionless square; keep it axis-aligned so
          // the cubes don't tilt at corners (atan2 of the smoothed tangent
          // gives diagonal angles that read as broken visuals).
          sprite.setAngle(0);
        }

        // Invulnerable blinking effect
        if (snake.isInvulnerable) {
          sprite.setAlpha(Math.sin(this.time.now * 0.01) > 0 ? 1 : 0.3);
        } else {
          sprite.setAlpha(1);
        }
      }

      // === HAT RENDERING (player only) ===
      if (isPlayer && this.playerHatKey && sprites.length > 0) {
        const headSprite = sprites[0];
        if (headSprite.scene) {
          let hatSprite = this.hatSprites.get(id);
          if (!hatSprite) {
            hatSprite = this.add.image(0, 0, this.playerHatKey)
              .setOrigin(0.5, 0.5)
              .setDepth(isPlayer ? 15 : 8);
            this.hatSprites.set(id, hatSprite);
          }
          // Offset hat behind head center (opposite movement direction)
          const angle = headSprite.angle;
          const rad = Phaser.Math.DegToRad(angle);
          const backOffset = CELL_SIZE * 0.38;
          const hatX = headSprite.x - Math.cos(rad) * backOffset;
          const hatY = headSprite.y - Math.sin(rad) * backOffset;
          hatSprite.setPosition(hatX, hatY);
          // Scale: hat is 128px, head is 32px — match proportionally
          const hs = spriteScale * 0.35;
          // Mirror X when going left so hat doesn't appear flipped
          const facingLeft = (angle === 180);
          hatSprite.setScale(facingLeft ? -hs : hs, -hs);
          // Negative scaleY reverses visual rotation, so compensate:
          // Flipped hat brim points UP = head angle 270 at rotation 0
          hatSprite.setAngle(270 - angle);
          hatSprite.setAlpha(headSprite.alpha);
          hatSprite.setVisible(true);
        }
      } else {
        const existing = this.hatSprites.get(id);
        if (existing) existing.setVisible(false);
      }

      // === NAME LABEL above head ===
      if (sprites.length > 0 && sprites[0].scene) {
        const headSprite = sprites[0];
        let label = this.nameLabels.get(id);
        if (!label) {
          label = this.add.text(0, 0, snake.playerId, {
            fontFamily: 'monospace',
            fontSize: '7px',
            color: isPlayer ? '#ffffff' : '#888899',
            stroke: '#000000',
            strokeThickness: 2,
          }).setOrigin(0.5, 1).setDepth(isPlayer ? 16 : 7);
          this.nameLabels.set(id, label);
        }
        label.setPosition(headSprite.x, headSprite.y - CELL_SIZE * 0.6);
        label.setAlpha(headSprite.alpha * 0.7);
      }
    }
  }

  /** Deterministic hash from coin ID for random-looking offsets */
  private static coinHash(id: string): number {
    let h = 0;
    for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
    return Math.abs(h);
  }

  private renderCoins(): void {
    // Remove coins no longer in state
    for (const [id, sprite] of this.coinSprites) {
      if (!this.latestCoins[id]) {
        sprite.destroy();
        this.coinSprites.delete(id);
      }
    }

    // Add/update coins — organic: small, offset, rotated, floating
    const baseScale = CELL_SIZE / 64; // smaller than before (was /42)

    for (const [id, coin] of Object.entries(this.latestCoins)) {
      let sprite = this.coinSprites.get(id);
      if (!sprite) {
        const textureKey = coin.tier === 'JACKPOT' ? 'coin_jackpot' :
          coin.tier === 'RARE' ? 'coin_rare' : 'coin_common';

        const hash = GameScene.coinHash(id);

        // Tier-based scale: JACKPOT bigger, RARE slightly bigger
        const tierScale = coin.tier === 'JACKPOT' ? 1.4 :
          coin.tier === 'RARE' ? 1.2 : 1.0;

        // Centered in tile (easier to follow patterns)
        const x = coin.position.x * CELL_SIZE + CELL_SIZE / 2;
        const y = coin.position.y * CELL_SIZE + CELL_SIZE / 2;

        sprite = this.add.image(x, y, textureKey)
          .setDepth(3)
          .setScale(baseScale * tierScale);

        // Random rotation (±10°)
        const angle = ((hash >> 6) % 20) - 10;
        sprite.setAngle(angle);

        // Floating bob — each coin has different timing (desynchronized)
        const bobDuration = 1200 + (hash % 800);
        this.tweens.add({
          targets: sprite,
          y: y - 1.5,
          duration: bobDuration,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
          delay: hash % 600, // staggered start
        });

        // JACKPOT coins pulse alpha
        if (coin.tier === 'JACKPOT') {
          this.tweens.add({
            targets: sprite,
            alpha: { from: 0.75, to: 1 },
            duration: 400,
            yoyo: true,
            repeat: -1,
          });
        }

        this.coinSprites.set(id, sprite);
      }
    }
  }

  private renderZones(): void {
    const portalScale = (CELL_SIZE * 2) / 64;

    // Detect portals that just disappeared (consumed by a snake)
    for (const id of this.knownActivePortals) {
      const zone = this.latestZones.find(z => z.id === id);
      if (!zone || !zone.isActive) {
        // Portal was consumed — play dissolve effect
        const sprite = this.portalSprites.get(id);
        if (sprite) {
          this.playPortalDissolve(sprite.x, sprite.y);
          sprite.destroy();
          this.portalSprites.delete(id);
        }
        // Clean up glow and particles
        this.portalGlows.get(id)?.destroy();
        this.portalGlows.delete(id);
        this.portalParticles.get(id)?.destroy();
        this.portalParticles.delete(id);
        this.knownActivePortals.delete(id);
      }
    }

    // Add/update portals
    for (const zone of this.latestZones) {
      if (!zone.isActive) continue;

      const cx = zone.position.x * CELL_SIZE + CELL_SIZE;
      const cy = zone.position.y * CELL_SIZE + CELL_SIZE;

      // Track as active
      this.knownActivePortals.add(zone.id);

      // Create sprite if new
      if (!this.portalSprites.has(zone.id)) {
        const sprite = this.add.sprite(cx, cy, 'portal')
          .setOrigin(0.5, 0.5)
          .setScale(portalScale)
          .setDepth(2)
          .play('portal_active');

        this.portalSprites.set(zone.id, sprite);

        // Breathing tween — scale pulses
        this.tweens.add({
          targets: sprite,
          scaleX: portalScale * 1.12,
          scaleY: portalScale * 1.12,
          duration: 1200,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        });

        // Glow light under portal — pre-rendered texture, breathing alpha
        // tween instead of redrawing concentric circles every frame.
        const glowKey = this.ensurePortalGlowTexture();
        const glow = this.add.image(cx, cy, glowKey)
          .setDisplaySize(CELL_SIZE * 6, CELL_SIZE * 6)
          .setDepth(1)
          .setBlendMode(Phaser.BlendModes.ADD)
          .setAlpha(0.85);
        this.portalGlows.set(zone.id, glow);
        this.tweens.add({
          targets: glow,
          alpha: 1.0,
          scale: glow.scale * 1.08,
          duration: 1100,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        });

        // Ambient particles orbiting the portal
        const emitter = this.add.particles(cx, cy, 'portal_particle', {
          speed: { min: 5, max: 15 },
          angle: { min: 0, max: 360 },
          scale: { start: 0.8, end: 0 },
          alpha: { start: 0.7, end: 0 },
          lifespan: { min: 1500, max: 3000 },
          frequency: 150,
          quantity: 1,
          tint: [0x9d00ff, 0xffd700, 0xffffff],
          blendMode: 'ADD',
          emitZone: {
            type: 'edge',
            source: new Phaser.Geom.Circle(0, 0, CELL_SIZE * 1.2),
            quantity: 12,
          },
        });
        emitter.setDepth(3);
        this.portalParticles.set(zone.id, emitter);
      }
    }
  }

  private startLatencyMeter(): void {
    this.client.on('latency_pong', (data: any) => {
      const sentAt = typeof data?.clientSentAt === 'number' ? data.clientSentAt : null;
      if (sentAt === null) return;

      const latencyMs = performance.now() - sentAt;
      if (this.lastLatencyMs !== null) {
        const sampleJitter = Math.abs(latencyMs - this.lastLatencyMs);
        this.latencyJitterMs = this.latencyJitterMs * 0.8 + sampleJitter * 0.2;
      }
      this.lastLatencyMs = latencyMs;

      this.game.events.emit('latencyUpdate', {
        pingMs: Math.round(latencyMs),
        jitterMs: Math.round(this.latencyJitterMs),
      });
    });

    this.latencyTimer = this.time.addEvent({
      delay: 1000,
      loop: true,
      callback: () => {
        this.client.sendLatencyPing(performance.now());
      },
    });
    this.client.sendLatencyPing(performance.now());
  }

  /**
   * Lazy-create a radial-gradient texture for the portal glow. The original
   * implementation redrew 9 concentric circles per portal per frame
   * (~270 fillCircle ops/sec with 3 active portals); this builds the gradient
   * once and reuses an Image with an alpha tween for the breathing effect.
   */
  private ensurePortalGlowTexture(): string {
    const key = 'portal_glow_tex';
    if (this.textures.exists(key)) return key;
    const size = 256;
    const tex = this.textures.createCanvas(key, size, size);
    if (!tex) return key;
    const ctx = tex.getContext();
    // Outer purple halo
    const outer = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    outer.addColorStop(0,    'rgba(153, 68, 255, 0.55)');
    outer.addColorStop(0.5,  'rgba(153, 68, 255, 0.25)');
    outer.addColorStop(1,    'rgba(153, 68, 255, 0.00)');
    ctx.fillStyle = outer;
    ctx.fillRect(0, 0, size, size);
    // Inner gold core
    const inner = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 5);
    inner.addColorStop(0, 'rgba(255, 215, 0, 0.55)');
    inner.addColorStop(1, 'rgba(255, 215, 0, 0.00)');
    ctx.fillStyle = inner;
    ctx.fillRect(0, 0, size, size);
    tex.refresh();
    return key;
  }

  /** Dissolve effect when portal is consumed */
  private playPortalDissolve(x: number, y: number): void {
    // Screen flash
    this.cameras.main.flash(200, 150, 100, 255, false);
    this.cameras.main.shake(150, 0.008);

    // Burst of particles
    const burst = this.add.particles(x, y, 'portal_particle', {
      speed: { min: 30, max: 80 },
      angle: { min: 0, max: 360 },
      scale: { start: 1.2, end: 0 },
      alpha: { start: 1, end: 0 },
      lifespan: { min: 400, max: 900 },
      quantity: 25,
      tint: [0x9d00ff, 0xffd700, 0xffffff, 0x6600cc],
      blendMode: 'ADD',
      emitting: false,
    });
    burst.setDepth(10);
    burst.explode(25);

    // Expanding ring effect
    const ring = this.add.circle(x, y, 2, 0x9944ff, 0.6).setDepth(10);
    this.tweens.add({
      targets: ring,
      radius: CELL_SIZE * 4,
      alpha: 0,
      duration: 500,
      ease: 'Power2',
      onComplete: () => ring.destroy(),
    });

    // Clean up burst after animation
    this.time.delayedCall(1000, () => burst.destroy());
  }

  private createAmbientEffects(): void {
    const worldW = this.gridWidth * CELL_SIZE;
    const worldH = this.gridHeight * CELL_SIZE;

    // --- Ambient glow spots (casino floor lights; cold aurora in tournament) ---
    const glowColors = this.tournamentMode
      ? [0x2a6f9d, 0x4ec3e0, 0x1d4e6b, 0x88d0e8, 0x2a6f9d, 0x4ec3e0]
      : [0x9d00ff, 0x00ffd1, 0x4400aa, 0x006655, 0x9d00ff, 0x00ffd1];
    const glowCount = 8;
    const margin = CELL_SIZE * 6;

    for (let i = 0; i < glowCount; i++) {
      const gx = margin + Math.random() * (worldW - margin * 2);
      const gy = margin + Math.random() * (worldH - margin * 2);
      const color = glowColors[i % glowColors.length];
      const radius = 40 + Math.random() * 30;

      const glow = this.add.graphics();
      // Draw soft radial glow using concentric circles
      const rings = 8;
      for (let r = rings; r >= 0; r--) {
        const progress = r / rings;
        const alpha = (1 - progress) * 0.04; // very subtle, max ~0.04
        const ringRadius = radius * progress;
        glow.fillStyle(color, alpha);
        glow.fillCircle(gx, gy, ringRadius);
      }
      glow.setDepth(0);
      glow.setBlendMode(Phaser.BlendModes.ADD);
    }

    // --- Floating dust particles (casino atmosphere) ---
    // Create a tiny particle texture
    const particleGfx = this.add.graphics();
    particleGfx.fillStyle(0xffd700, 1);
    particleGfx.fillRect(0, 0, 2, 2);
    particleGfx.generateTexture('dust_particle', 2, 2);
    particleGfx.destroy();

    if (this.tournamentMode) {
      // Falling snow — drifts down from above the field. NORMAL blend so the
      // flakes read as opaque snow rather than additive glow.
      const snow = this.add.particles(0, 0, 'dust_particle', {
        x: { min: 0, max: worldW },
        y: -10,
        scale: { min: 0.5, max: 1.4 },
        alpha: { start: 0.7, end: 0.85 },
        lifespan: { min: 6000, max: 10000 },
        speed: { min: 12, max: 38 },
        angle: { min: 80, max: 100 },
        frequency: 120,
        quantity: 1,
        tint: [0xffffff, 0xeaf6ff, 0xcfe8f7],
      });
      snow.setDepth(1);
    } else {
      // Gold dust — slow floating particles (casino atmosphere)
      const goldDust = this.add.particles(0, 0, 'dust_particle', {
        x: { min: 0, max: worldW },
        y: { min: 0, max: worldH },
        scale: { min: 0.3, max: 0.8 },
        alpha: { start: 0, end: 0.3, ease: 'Sine.easeInOut' },
        lifespan: { min: 4000, max: 8000 },
        speed: { min: 2, max: 8 },
        angle: { min: 250, max: 290 },
        frequency: 200,
        quantity: 1,
        tint: [0xffd700, 0xffffff, 0x9d00ff],
        blendMode: 'ADD',
      });
      goldDust.setDepth(1);
    }

    // --- Vignette overlay (follows camera) ---
    const vignetteW = this.scale.width;
    const vignetteH = this.scale.height;
    const vignette = this.add.graphics();
    vignette.setScrollFactor(0);
    vignette.setDepth(50);

    // Draw radial vignette using concentric rectangles with increasing opacity
    const steps = 12;
    for (let i = 0; i < steps; i++) {
      const progress = i / steps;
      const alpha = progress * progress * 0.6; // quadratic curve, max 0.6 opacity at edges
      const inset = (1 - progress) * Math.min(vignetteW, vignetteH) * 0.4;

      vignette.fillStyle(0x000000, alpha);
      // Top strip
      vignette.fillRect(0, 0, vignetteW, inset);
      // Bottom strip
      vignette.fillRect(0, vignetteH - inset, vignetteW, inset);
      // Left strip
      vignette.fillRect(0, 0, inset, vignetteH);
      // Right strip
      vignette.fillRect(vignetteW - inset, 0, inset, vignetteH);
    }

    // Handle resize
    this.scale.on('resize', () => {
      // Recreate vignette on resize
      const newW = this.scale.width;
      const newH = this.scale.height;
      vignette.clear();
      for (let i = 0; i < steps; i++) {
        const progress = i / steps;
        const alpha = progress * progress * 0.6;
        const inset = (1 - progress) * Math.min(newW, newH) * 0.4;
        vignette.fillStyle(0x000000, alpha);
        vignette.fillRect(0, 0, newW, inset);
        vignette.fillRect(0, newH - inset, newW, inset);
        vignette.fillRect(0, 0, inset, newH);
        vignette.fillRect(newW - inset, 0, inset, newH);
      }
    });
  }

  private drawGrid(): void {
    if (!this.gridGraphics) return;
    this.gridGraphics.clear();

    const w = this.gridWidth * CELL_SIZE;
    const h = this.gridHeight * CELL_SIZE;

    // Tournament = icy "snowy night" palette; arena keeps the dark casino look.
    const pal = this.tournamentMode
      ? { shadeA: 0x0f1f2e, shadeB: 0x12273a, line: 0x3a6f92, lineA: 0.45, dot: 0x4a7e9e }
      : { shadeA: 0x0d0d1a, shadeB: 0x0f0f1e, line: 0x1a1a2e, lineA: 0.6,  dot: 0x1e1e30 };

    // Dark cell fills — alternating very subtly for depth
    for (let x = 0; x < this.gridWidth; x++) {
      for (let y = 0; y < this.gridHeight; y++) {
        const shade = ((x + y) % 2 === 0) ? pal.shadeA : pal.shadeB;
        this.gridGraphics.fillStyle(shade, 1);
        this.gridGraphics.fillRect(x * CELL_SIZE, y * CELL_SIZE, CELL_SIZE, CELL_SIZE);
      }
    }

    // Grid lines — subtle dark navy (frosty blue in tournament)
    this.gridGraphics.lineStyle(1, pal.line, pal.lineA);

    // Vertical lines
    for (let x = 0; x <= this.gridWidth; x++) {
      this.gridGraphics.moveTo(x * CELL_SIZE, 0);
      this.gridGraphics.lineTo(x * CELL_SIZE, h);
    }
    // Horizontal lines
    for (let y = 0; y <= this.gridHeight; y++) {
      this.gridGraphics.moveTo(0, y * CELL_SIZE);
      this.gridGraphics.lineTo(w, y * CELL_SIZE);
    }
    this.gridGraphics.strokePath();

    // Inner cell dots at intersections for extra detail
    this.gridGraphics.fillStyle(pal.dot, 0.4);
    for (let x = 1; x < this.gridWidth; x++) {
      for (let y = 1; y < this.gridHeight; y++) {
        this.gridGraphics.fillRect(x * CELL_SIZE - 1, y * CELL_SIZE - 1, 2, 2);
      }
    }

    this.gridGraphics.setDepth(0);
  }

  private drawWalls(): void {
    if (!this.wallGraphics) return;
    this.wallGraphics.clear();
    // Icy walls in tournament; plain dark grey in the arena.
    this.wallGraphics.fillStyle(this.tournamentMode ? 0x7fa8c4 : 0x333333, 1);

    for (let x = 0; x < this.gridWidth; x++) {
      for (let y = 0; y < this.gridHeight; y++) {
        if (
          x <= this.borderOffset || x >= this.gridWidth - 1 - this.borderOffset ||
          y <= this.borderOffset || y >= this.gridHeight - 1 - this.borderOffset
        ) {
          this.wallGraphics.fillRect(x * CELL_SIZE, y * CELL_SIZE, CELL_SIZE, CELL_SIZE);
        }
      }
    }
    this.wallGraphics.setDepth(1);
  }

  /** Snake id the camera should follow: the local player normally, or — when
   *  spectating — the requested agent's snake, falling back to the live arena
   *  leader (highest coins+banked among alive snakes) when it's absent/dead. */
  private cameraTargetId(): string {
    if (!this.spectateMode) return this.mySnakeId;
    const want = this.spectateSnakeId;
    if (want && this.latestSnakes[want]?.isAlive) return want;
    let best = '';
    let bestScore = -1;
    for (const [id, snake] of Object.entries(this.latestSnakes)) {
      const s = snake as SnakeData;
      if (!s.isAlive) continue;
      const score = (s.coins ?? 0) + (s.totalBanked ?? 0);
      if (score > bestScore) { bestScore = score; best = id; }
    }
    return best;
  }

  private followPlayerSnake(): void {
    const targetId = this.cameraTargetId();
    const mySnake = this.latestSnakes[targetId];

    if (mySnake?.isAlive && mySnake.segments.length > 0) {
      this.lastKnownHeadPos = { ...mySnake.segments[0] };
    }

    // Target position: use interpolated head sprite position if available
    let targetX: number;
    let targetY: number;

    const headSprites = this.snakeSprites.get(targetId);
    if (headSprites && headSprites.length > 0 && headSprites[0].scene) {
      // Use the already-interpolated sprite position
      targetX = headSprites[0].x;
      targetY = headSprites[0].y;
    } else if (this.lastKnownHeadPos) {
      targetX = this.lastKnownHeadPos.x * CELL_SIZE + CELL_SIZE / 2;
      targetY = this.lastKnownHeadPos.y * CELL_SIZE + CELL_SIZE / 2;
    } else {
      return;
    }

    // Spectator: subtle pulsing ring under the followed snake's head.
    if (this.spectateMode) this.updateSpectateRing(targetId, targetX, targetY);

    // Smooth camera with lerp (0 = first frame, snap immediately)
    if (this.cameraX === 0 && this.cameraY === 0) {
      this.cameraX = targetX;
      this.cameraY = targetY;
    } else {
      const lerpFactor = 0.15;
      this.cameraX += (targetX - this.cameraX) * lerpFactor;
      this.cameraY += (targetY - this.cameraY) * lerpFactor;
    }

    this.cameras.main.centerOn(this.cameraX, this.cameraY);
  }

  /** Subtle pulsing highlight ring on the snake the spectator is following. */
  private updateSpectateRing(targetId: string, x: number, y: number): void {
    const target = this.latestSnakes[targetId];
    if (!targetId || !target?.isAlive) {
      this.spectateRing?.setVisible(false);
      return;
    }
    if (!this.spectateRing) {
      this.spectateRing = this.add.circle(x, y, CELL_SIZE, 0x66d9ff, 0)
        .setStrokeStyle(2, 0x66d9ff, 0.9)
        .setDepth(15);
    }
    const pulse = CELL_SIZE * (1.0 + 0.18 * Math.sin(this.time.now * 0.006));
    this.spectateRing.setPosition(x, y).setRadius(pulse).setVisible(true);
  }

  // --- Popups (now handled by JuiceEffects) ---

  /* Old popup methods removed — replaced by JuiceEffects and AnnouncerOverlay */

  /** 3-2-1 countdown with snake blinking */
  private showCountdown(): void {
    const { width, height } = this.scale;

    // Input works during countdown (so player can steer away from walls)
    this.gameStarted = false;

    // HUDScene owns the keyboard hint overlay (its camera is zoom 1, so
    // setScrollFactor(0) actually pins to screen-space — GameScene's
    // 2x camera renders fixed sprites at the wrong place).
    //
    // Small delay so HUDScene has a frame or two to wire its listener
    // — scene.launch('HUDScene') happens later in this same create()
    // call (further down), and Phaser runs scene create() asynchronously,
    // so the listener won't exist if we emit synchronously.
    this.time.delayedCall(150, () => this.game.events.emit('countdown:start'));

    const numbers = ['3', '2', '1', 'GO!'];
    const colors = ['#ffffff', '#ffdd00', '#ff8800', '#00ff88'];
    const sizes = ['48px', '52px', '56px', '64px'];

    // Slow motion during countdown (half speed)
    this.time.timeScale = 0.5;

    numbers.forEach((num, i) => {
      this.time.delayedCall(i * 800, () => {
        const text = this.add.text(width / 2, height / 2, num, {
          fontFamily: 'monospace',
          fontSize: sizes[i],
          color: colors[i],
          stroke: '#000000',
          strokeThickness: 6,
          fontStyle: 'bold',
        }).setOrigin(0.5).setDepth(200).setScrollFactor(0).setScale(2).setAlpha(0);

        // Slam in
        this.tweens.add({
          targets: text,
          scale: 1,
          alpha: 1,
          duration: 200,
          ease: 'Back.easeOut',
          onComplete: () => {
            sfx(this, num === 'GO!' ? 'countdown_go' : 'countdown_tick');
            this.cameras.main.shake(40, 0.003);

            // Fade out
            this.tweens.add({
              targets: text,
              alpha: 0,
              scale: 0.5,
              duration: 400,
              delay: 200,
              onComplete: () => text.destroy(),
            });
          },
        });

        // On "GO!" — restore full speed
        if (num === 'GO!') {
          this.gameStarted = true;
          this.time.timeScale = 1;
          this.game.events.emit('countdown:end');
        }
      });
    });

    // Snake blinking during countdown
    this.time.addEvent({
      delay: 300,
      repeat: 7,
      callback: () => {
        const mySprites = this.snakeSprites.get(this.mySnakeId);
        if (mySprites) {
          mySprites.forEach(s => s.setAlpha(s.alpha < 0.5 ? 1 : 0.3));
        }
      },
    });

    // Ensure snake is fully visible after countdown
    this.time.delayedCall(numbers.length * 800, () => {
      const mySprites = this.snakeSprites.get(this.mySnakeId);
      if (mySprites) {
        mySprites.forEach(s => s.setAlpha(1));
      }
    });
  }

  /** Show spectator UI when player dies */
  private showDeathUI(): void {
    const { width, height } = this.scale;

    // "SPECTATING" label
    this.time.delayedCall(1500, () => {
      const spectateText = this.add.text(width / 2, height * 0.15, 'SPECTATING', {
        fontFamily: 'monospace', fontSize: '18px', color: '#666677',
        stroke: '#000', strokeThickness: 3,
      }).setOrigin(0.5).setDepth(200).setScrollFactor(0).setAlpha(0);

      this.tweens.add({ targets: spectateText, alpha: 0.7, duration: 400 });

      // "Back to Menu" button
      const backBtn = this.add.text(width / 2, height * 0.88, '[ BACK TO MENU ]', {
        fontFamily: 'monospace', fontSize: '16px', color: '#888899',
        stroke: '#000', strokeThickness: 3,
      }).setOrigin(0.5).setDepth(200).setScrollFactor(0).setAlpha(0)
        .setInteractive({ useHandCursor: true });

      this.tweens.add({ targets: backBtn, alpha: 1, duration: 400 });

      backBtn.on('pointerover', () => backBtn.setColor('#ffffff'));
      backBtn.on('pointerout', () => backBtn.setColor('#888899'));
      backBtn.on('pointerdown', () => {
        this.scene.stop('HUDScene');
        this.cameras.main.fadeOut(300, 0, 0, 0);
        this.cameras.main.once('camerafadeoutcomplete', () => {
          this.scene.start('MainMenuScene');
        });
      });
    });
  }

  private showDeathOverlay(totalLost: number, coinsInHand: number, coinsBanked: number): void {
    const { width, height } = this.scale;
    const cx = width / 2;

    sfx(this, 'death_overlay');

    // Dark overlay
    const overlay = this.add.rectangle(cx, height / 2, width, height, 0x000000, 0.78)
      .setScrollFactor(0).setDepth(300);
    overlay.setAlpha(0);
    this.tweens.add({ targets: overlay, alpha: 1, duration: 400 });

    // === Layout: center-anchored, fixed pixel offsets ===
    // Compute total content height and start at center - half so it never overflows.
    const titleH = 50;
    const lossLineH = 22;
    const totalLineH = 30;
    const gap = 12;
    const chartH = 80;
    const playBtnH = 32;
    const menuBtnH = 18;
    const lossCount = (coinsInHand > 0 ? 1 : 0) + (coinsBanked > 0 ? 1 : 0);
    const totalContentH =
      titleH + gap + (lossCount * lossLineH) + (totalLost > 0 ? gap + totalLineH : 0) +
      gap + chartH + gap + playBtnH + 8 + menuBtnH;

    let y = (height - totalContentH) / 2;
    // Keep a safe top margin on tall screens
    y = Math.max(y, height * 0.08);

    // "YOU DIED"
    const title = this.add.text(cx, y + titleH / 2, 'YOU DIED', {
      fontFamily: 'monospace', fontSize: '38px', color: '#ff2222',
      stroke: '#000', strokeThickness: 5, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(301).setScrollFactor(0).setAlpha(0).setScale(1.4);
    this.tweens.add({ targets: title, alpha: 1, scale: 1, duration: 400, delay: 200, ease: 'Back.easeOut' });
    y += titleH + gap;

    if (coinsInHand > 0) {
      const t = this.add.text(cx, y + lossLineH / 2, `COINS IN HAND: -${coinsInHand}`, {
        fontFamily: 'monospace', fontSize: '14px', color: '#ff6644',
        stroke: '#000', strokeThickness: 3,
      }).setOrigin(0.5).setDepth(301).setScrollFactor(0).setAlpha(0);
      this.tweens.add({ targets: t, alpha: 1, duration: 300, delay: 500 });
      y += lossLineH;
    }

    if (coinsBanked > 0) {
      const t = this.add.text(cx, y + lossLineH / 2, `BANKED COINS LOST: -${coinsBanked}`, {
        fontFamily: 'monospace', fontSize: '14px', color: '#ff4444',
        stroke: '#000', strokeThickness: 3,
      }).setOrigin(0.5).setDepth(301).setScrollFactor(0).setAlpha(0);
      this.tweens.add({ targets: t, alpha: 1, duration: 300, delay: 600 });
      y += lossLineH;
    }

    if (totalLost > 0) {
      y += gap;
      const t = this.add.text(cx, y + totalLineH / 2, `TOTAL LOST: ${totalLost}`, {
        fontFamily: 'monospace', fontSize: '20px', color: '#ff2222',
        stroke: '#000', strokeThickness: 4, fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(301).setScrollFactor(0).setAlpha(0);
      this.tweens.add({ targets: t, alpha: 1, duration: 300, delay: 700 });
      y += totalLineH;
    } else if (lossCount === 0) {
      this.add.text(cx, y, 'NOTHING TO LOSE', {
        fontFamily: 'monospace', fontSize: '14px', color: '#666677',
      }).setOrigin(0.5).setDepth(301).setScrollFactor(0);
      y += lossLineH;
    }

    // Crypto chart
    y += gap;
    const chartW = Math.min(260, width * 0.55);
    const chartContainer = this.coinChart.render(this, cx, y + chartH / 2, chartW, chartH, 301);
    chartContainer.setAlpha(0);
    this.tweens.add({ targets: chartContainer, alpha: 1, duration: 400, delay: 800 });
    y += chartH + gap;

    // PLAY AGAIN
    const playBtn = this.add.text(cx, y + playBtnH / 2, '[ PLAY AGAIN ]', {
      fontFamily: 'monospace', fontSize: '22px', color: '#00ff88',
      stroke: '#000', strokeThickness: 5, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(301).setScrollFactor(0).setAlpha(0)
      .setInteractive({ useHandCursor: true });
    this.tweens.add({ targets: playBtn, alpha: 1, duration: 400, delay: 1000 });
    this.tweens.add({
      targets: playBtn, scale: 1.05, duration: 600, yoyo: true, repeat: -1, delay: 1400, ease: 'Sine.easeInOut',
    });
    playBtn.on('pointerover', () => { sfx(this, 'button_hover'); playBtn.setColor('#ffffff'); });
    playBtn.on('pointerout', () => playBtn.setColor('#00ff88'));
    playBtn.on('pointerdown', () => {
      sfx(this, 'button_click');
      this.cameras.main.fadeOut(300, 0, 0, 0);
      this.cameras.main.once('camerafadeoutcomplete', () => {
        this.scene.start('PerkSelectScene', { perks: this.getRandomPerks() });
      });
    });
    y += playBtnH + 8;

    // Back to Menu
    const menuBtn = this.add.text(cx, y + menuBtnH / 2, 'Back to Menu', {
      fontFamily: 'monospace', fontSize: '12px', color: '#888899',
      stroke: '#000', strokeThickness: 2,
    }).setOrigin(0.5).setDepth(301).setScrollFactor(0).setAlpha(0)
      .setInteractive({ useHandCursor: true });
    this.tweens.add({ targets: menuBtn, alpha: 1, duration: 400, delay: 1200 });
    menuBtn.on('pointerover', () => { sfx(this, 'button_hover'); menuBtn.setColor('#ffffff'); });
    menuBtn.on('pointerout', () => menuBtn.setColor('#888899'));
    menuBtn.on('pointerdown', () => {
      sfx(this, 'button_click');
      this.cameras.main.fadeOut(300, 0, 0, 0);
      this.cameras.main.once('camerafadeoutcomplete', () => {
        this.scene.start('MainMenuScene');
      });
    });
  }

  /**
   * End-of-demo overlay. Replaces both ChestCeremonyScene and showDeathOverlay
   * for demo runs — no rewards to ceremonialize, just a run summary and a
   * conversion CTA pointing back at the wallet flow.
   */
  private showDemoEndOverlay(params: { coinsBanked: number; kind: 'died' | 'extracted' }): void {
    const { width, height } = this.scale;
    const cx = width / 2;
    const cy = height / 2;

    sfx(this, 'death_overlay');

    // Dim
    this.add.rectangle(cx, cy, width, height, 0x000000, 0.85)
      .setScrollFactor(0).setDepth(300);

    // Title
    const titleText = params.kind === 'extracted' ? 'DEMO COMPLETE' : 'DEMO OVER';
    const titleColor = params.kind === 'extracted' ? '#00ff88' : '#ffd166';
    const title = this.add.text(cx, cy - 110, titleText, {
      fontFamily: 'monospace', fontSize: '36px', color: titleColor,
      stroke: '#000', strokeThickness: 5, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(301).setScrollFactor(0).setAlpha(0).setScale(1.3);
    this.tweens.add({ targets: title, alpha: 1, scale: 1, duration: 400, ease: 'Back.easeOut' });

    // Run summary
    const summary = this.add.text(cx, cy - 60,
      `You collected ${params.coinsBanked} coins this run.`, {
      fontFamily: 'monospace', fontSize: '14px', color: '#cccccc',
      stroke: '#000', strokeThickness: 2,
    }).setOrigin(0.5).setDepth(301).setScrollFactor(0).setAlpha(0);
    this.tweens.add({ targets: summary, alpha: 1, duration: 400, delay: 300 });

    // CTA copy
    const cta = this.add.text(cx, cy - 20,
      'Liked it? Connect a wallet to play for real and keep your rewards.', {
      fontFamily: 'monospace', fontSize: '12px', color: '#888899',
      align: 'center', wordWrap: { width: 460 }, lineSpacing: 4,
    }).setOrigin(0.5).setDepth(301).setScrollFactor(0).setAlpha(0);
    this.tweens.add({ targets: cta, alpha: 1, duration: 400, delay: 500 });

    // [ TRY AGAIN ] (back to demo perk pick)
    const tryBtn = this.add.text(cx, cy + 40, '[ TRY AGAIN ]', {
      fontFamily: 'monospace', fontSize: '20px', color: '#ffd166',
      stroke: '#000', strokeThickness: 4, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(301).setScrollFactor(0).setAlpha(0)
      .setInteractive({ useHandCursor: true });
    this.tweens.add({ targets: tryBtn, alpha: 1, duration: 400, delay: 700 });
    tryBtn.on('pointerover', () => { sfx(this, 'button_hover'); tryBtn.setColor('#ffffff'); });
    tryBtn.on('pointerout', () => tryBtn.setColor('#ffd166'));
    tryBtn.on('pointerdown', () => {
      sfx(this, 'button_click');
      this.cameras.main.fadeOut(300, 0, 0, 0);
      this.cameras.main.once('camerafadeoutcomplete', () => {
        this.scene.start('PerkSelectScene', { perks: this.getRandomPerks(), demoMode: true });
      });
    });

    // [ CONNECT WALLET & PLAY FOR REAL ] (primary CTA)
    const realBtn = this.add.text(cx, cy + 90, '[ CONNECT WALLET & PLAY FOR REAL ]', {
      fontFamily: 'monospace', fontSize: '18px', color: '#00ff88',
      stroke: '#000', strokeThickness: 5, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(301).setScrollFactor(0).setAlpha(0)
      .setInteractive({ useHandCursor: true });
    this.tweens.add({ targets: realBtn, alpha: 1, duration: 400, delay: 900 });
    this.tweens.add({
      targets: realBtn, scale: 1.05, duration: 700, yoyo: true, repeat: -1, delay: 1300,
      ease: 'Sine.easeInOut',
    });
    realBtn.on('pointerover', () => { sfx(this, 'button_hover'); realBtn.setColor('#ffffff'); });
    realBtn.on('pointerout', () => realBtn.setColor('#00ff88'));
    realBtn.on('pointerdown', () => {
      sfx(this, 'button_click');
      this.cameras.main.fadeOut(300, 0, 0, 0);
      this.cameras.main.once('camerafadeoutcomplete', () => {
        this.scene.start('MainMenuScene');
      });
    });

    // Neutral escape — same destination as the CTA, but no commitment framing
    const menuBtn = this.add.text(cx, cy + 140, 'Back to Menu', {
      fontFamily: 'monospace', fontSize: '12px', color: '#888899',
      stroke: '#000', strokeThickness: 2,
    }).setOrigin(0.5).setDepth(301).setScrollFactor(0).setAlpha(0)
      .setInteractive({ useHandCursor: true });
    this.tweens.add({ targets: menuBtn, alpha: 1, duration: 400, delay: 1100 });
    menuBtn.on('pointerover', () => { sfx(this, 'button_hover'); menuBtn.setColor('#ffffff'); });
    menuBtn.on('pointerout', () => menuBtn.setColor('#888899'));
    menuBtn.on('pointerdown', () => {
      sfx(this, 'button_click');
      this.cameras.main.fadeOut(300, 0, 0, 0);
      this.cameras.main.once('camerafadeoutcomplete', () => {
        this.scene.start('MainMenuScene');
      });
    });
  }

  /** Load snake skin preferences from localStorage */
  private loadSkinPreferences(): void {
    const HAT_KEYS: Record<string, string> = {
      wizard: 'hat_wizard', crown: 'hat_crown', cowboy: 'hat_cowboy',
      viking: 'hat_viking', party: 'hat_party', cap: 'hat_cap',
      knight: 'hat_knight', pepper: 'hat_pepper',
    };
    try {
      const saved = localStorage.getItem('snake_skin');
      if (saved) {
        const prefs = JSON.parse(saved);
        const hatId = prefs.hatId ?? 'none';
        this.playerHatKey = HAT_KEYS[hatId] ?? '';
      }
    } catch {
      // ignore
    }
  }

  private getRandomPerks() {
    return getRandomPerks(3);
  }

  /** Convert a silent freeze (dropped socket / dead state feed) into a
   *  visible, reportable event: log full context to the console, show an
   *  overlay with a copyable incident ID the player can paste to support,
   *  then offer a clean exit to the menu. Fires at most once per match. */
  private handleConnectionLost(info: { reason: string; code?: number; message?: string; sinceMs?: number }): void {
    if (this.connectionLost) return;
    this.connectionLost = true;

    const incidentId =
      `PMS-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;
    const wallet = (window as any).__currentUser?.walletAddress ?? null;
    const context = {
      incidentId,
      reason: info.reason,
      code: info.code ?? null,
      message: info.message ?? null,
      sinceMs: info.sinceMs ?? null,
      demoMode: this.demoMode,
      snakeId: this.mySnakeId || null,
      wallet,
      at: new Date().toISOString(),
    };
    console.error(`[connection-lost] ${incidentId}`, context);
    track('connection_lost', context);

    // Persist server-side so support can look the id up (Supabase +
    // Railway log). Fire-and-forget; keepalive lets it survive the
    // imminent scene transition.
    try {
      void fetch(`${SERVER_HTTP_URL}/api/client-incident`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
        body: JSON.stringify({
          incidentId,
          kind: 'connection_lost',
          reason: info.reason,
          code: info.code ?? null,
          message: info.message ?? null,
          sinceMs: info.sinceMs ?? null,
          demoMode: this.demoMode,
          snakeId: this.mySnakeId || null,
          wallet,
        }),
      });
    } catch { /* fire-and-forget */ }

    // Stop the (now useless) socket so onLeave/watchdog don't re-fire.
    this.client?.disconnect();

    this.showConnectionLostOverlay(incidentId);
  }

  /** Copy text with a fallback for when the async Clipboard API is blocked
   *  (no permission / not focused). Returns whether it succeeded. */
  private async copyToClipboard(text: string): Promise<boolean> {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch { /* fall through to legacy path */ }
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }

  /** End-of-run panel for tournament mode (one life, no chest ceremony).
   *  NOTE: each object gets its own setScrollFactor(0) — NOT a container —
   *  because under GameScene's zoomed/scrolled camera, container children
   *  keep scrollFactor 1 for input hit-testing and the button becomes
   *  unclickable (its hit area drifts with the camera). */
  private showTournamentResultOverlay(
    result: { runScore?: number; totalScore?: number; kills?: number; died?: boolean },
  ): void {
    const { width, height } = this.scale;
    const cx = width / 2;
    const died = !!result.died;

    this.add.rectangle(cx, height / 2, width, height, 0x000000, 0.85)
      .setScrollFactor(0).setDepth(100000).setInteractive();
    this.add.text(cx, height * 0.30, died ? 'ELIMINATED' : 'RUN COMPLETE', {
      fontFamily: 'monospace', fontSize: '38px', color: died ? '#ff5555' : '#00ff88',
      stroke: '#000', strokeThickness: 6, fontStyle: 'bold',
    }).setOrigin(0.5).setScrollFactor(0).setDepth(100001);
    const runScore = Math.max(0, result.runScore ?? 0);
    this.add.text(cx, height * 0.45,
      died ? 'No score this run (one life).' : `Run score:  ${runScore}`, {
      fontFamily: 'monospace', fontSize: '22px', color: '#ffd166', stroke: '#000', strokeThickness: 4,
    }).setOrigin(0.5).setScrollFactor(0).setDepth(100001);
    const hasScore = result.totalScore != null;
    if (hasScore) {
      this.add.text(cx, height * 0.53, `Tournament total:  ${result.totalScore}`, {
        fontFamily: 'monospace', fontSize: '16px', color: '#9fd3ff', stroke: '#000', strokeThickness: 3,
      }).setOrigin(0.5).setScrollFactor(0).setDepth(100001);

      // Share-on-X button — generates a styled card and opens the tweet
      // intent (server card lives on the Vercel /api/share function).
      const shareBtn = this.add.text(cx, height * 0.63, '[ SHARE ON X ]', {
        fontFamily: 'monospace', fontSize: '18px', color: '#66ccff',
        stroke: '#000', strokeThickness: 5, fontStyle: 'bold',
      }).setOrigin(0.5).setScrollFactor(0).setDepth(100001).setInteractive({ useHandCursor: true });
      shareBtn.on('pointerover', () => shareBtn.setColor('#ffffff'));
      shareBtn.on('pointerout', () => shareBtn.setColor('#66ccff'));
      shareBtn.on('pointerdown', () => {
        track('share_clicked', { type: 'tournament_result' });
        shareBtn.setText('[ GENERATING… ]').disableInteractive();
        void requestShare({
          type: 'tournament-result',
          runScore,
          totalScore: result.totalScore ?? 0,
          kills: result.kills ?? 0,
          died,
        })
          .then(() => shareBtn.setText('[ SHARED ✓ ]'))
          .catch(() => shareBtn.setText('[ SHARE FAILED — retry ]').setInteractive({ useHandCursor: true }));
      });
    }

    const menuBtn = this.add.text(cx, height * (hasScore ? 0.74 : 0.70), '[ BACK TO MENU ]', {
      fontFamily: 'monospace', fontSize: '20px', color: '#00ff88',
      stroke: '#000', strokeThickness: 5, fontStyle: 'bold',
    }).setOrigin(0.5).setScrollFactor(0).setDepth(100001).setInteractive({ useHandCursor: true });
    menuBtn.on('pointerover', () => menuBtn.setColor('#ffffff'));
    menuBtn.on('pointerout', () => menuBtn.setColor('#00ff88'));
    menuBtn.on('pointerdown', () => this.scene.start('MainMenuScene'));
  }

  private showConnectionLostOverlay(incidentId: string): void {
    const { width, height } = this.scale;
    const cx = width / 2;
    // Each object pinned individually (no container) so the buttons stay
    // clickable under GameScene's zoomed/scrolled camera — see note on
    // showTournamentResultOverlay.
    this.add.rectangle(cx, height / 2, width, height, 0x000000, 0.85)
      .setScrollFactor(0).setDepth(100000).setInteractive();
    this.add.text(cx, height * 0.34, 'CONNECTION LOST', {
      fontFamily: 'monospace', fontSize: '30px', color: '#ff5555',
      stroke: '#000', strokeThickness: 6, fontStyle: 'bold',
    }).setOrigin(0.5).setScrollFactor(0).setDepth(100001);
    this.add.text(cx, height * 0.45,
      'Lost connection to the game server.\nShare this ID with support so they can look it up:', {
      fontFamily: 'monospace', fontSize: '14px', color: '#cccccc', align: 'center',
      stroke: '#000', strokeThickness: 3,
    }).setOrigin(0.5).setScrollFactor(0).setDepth(100001);
    this.add.text(cx, height * 0.54, incidentId, {
      fontFamily: 'monospace', fontSize: '20px', color: '#ffd166', fontStyle: 'bold',
      stroke: '#000', strokeThickness: 4,
    }).setOrigin(0.5).setScrollFactor(0).setDepth(100001);

    const copyBtn = this.add.text(cx, height * 0.63, '[ COPY ID ]', {
      fontFamily: 'monospace', fontSize: '16px', color: '#66ccff',
      stroke: '#000', strokeThickness: 4,
    }).setOrigin(0.5).setScrollFactor(0).setDepth(100001).setInteractive({ useHandCursor: true });
    copyBtn.on('pointerover', () => copyBtn.setColor('#ffffff'));
    copyBtn.on('pointerout', () => copyBtn.setColor('#66ccff'));
    copyBtn.on('pointerdown', () => {
      void this.copyToClipboard(incidentId).then((ok) => {
        copyBtn.setText(ok ? '[ COPIED ✓ ]' : '[ COPY FAILED — select manually ]');
      });
    });

    const menuBtn = this.add.text(cx, height * 0.72, '[ BACK TO MENU ]', {
      fontFamily: 'monospace', fontSize: '18px', color: '#00ff88',
      stroke: '#000', strokeThickness: 5, fontStyle: 'bold',
    }).setOrigin(0.5).setScrollFactor(0).setDepth(100001).setInteractive({ useHandCursor: true });
    menuBtn.on('pointerover', () => menuBtn.setColor('#ffffff'));
    menuBtn.on('pointerout', () => menuBtn.setColor('#00ff88'));
    menuBtn.on('pointerdown', () => this.scene.start('MainMenuScene'));
  }

  shutdown(): void {
    this.connectionLost = false;
    this.lastUpdateAt = 0;
    this.pendingTournamentResult = null;
    this.client?.disconnect();
    this.inputManager?.destroy();
    this.snakeSprites.forEach(sprites => sprites.forEach(s => s.destroy()));
    this.snakeSprites.clear();
    this.hatSprites.forEach(s => s.destroy());
    this.hatSprites.clear();
    this.nameLabels.forEach(s => s.destroy());
    this.nameLabels.clear();
    this.coinSprites.forEach(s => s.destroy());
    this.coinSprites.clear();
    this.portalSprites.forEach(s => s.destroy());
    this.portalSprites.clear();
    this.portalGlows.forEach(g => g.destroy());
    this.portalGlows.clear();
    this.portalParticles.forEach(p => p.destroy());
    this.portalParticles.clear();
    this.knownActivePortals.clear();
    this.previousSnakes = {};
    this.lastStateTime = 0;
    this.latencyTimer?.remove(false);
    this.latencyTimer = null;
    this.cameraX = 0;
    this.cameraY = 0;
    this.frenzyZone?.destroy();
    this.frenzyZone = null;
  }
}
